import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  IconButton,
  Drawer,
  AppBar,
  TextField,
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  Chip,
  Popper,
  Grow,
  Paper,
  ClickAwayListener,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import TimelineIcon from '@mui/icons-material/Timeline';
import PieChartIcon from '@mui/icons-material/PieChart';
import InputAdornment from '@mui/material/InputAdornment';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import PublicIcon from '@mui/icons-material/Public';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import CategoryIcon from '@mui/icons-material/Category';
import BarChartIcon from '@mui/icons-material/BarChart';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BusinessIcon from '@mui/icons-material/Business';
import InsightsIcon from '@mui/icons-material/Insights';
import WorkIcon from '@mui/icons-material/Work';
import CodeIcon from '@mui/icons-material/Code';
import DonutSmallIcon from '@mui/icons-material/DonutSmall';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import GridOnIcon from '@mui/icons-material/GridOn';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useMediaQuery, useTheme } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useAuth0 } from '@auth0/auth0-react';
import ResearchDatasetMenu from './ResearchDatasetMenu';
import { isDocumentationRoute, getDocumentationTheme } from '../../pages/documentation/documentationTheme';

import '../../index.css';
import itemListData from '../../data/companylistsearch.json';
import MenuLink from './MenuLink';
import UserMenu from './UserMenu';
import {
  getHeaderBackdropSx,
  getHeaderFrameSx,
  getHeaderIconActionSx,
  getHeaderSearchContainerSx,
  getHeaderSearchInputSx,
  getHeaderShellSx,
  getHeaderSignInSx,
  getMobileHeaderSearchSx,
} from './headerChrome';
import CompanySearchOptionRow from '../common/CompanySearchOptionRow';
import { trackEvent } from '../../utils/analytics';
import CompanyTypeFilter from '../common/CompanyTypeFilter';
import CompanySearchDropdownPaper from '../common/CompanySearchDropdownPaper';
import {
  COMPANY_TYPE_FILTERS,
  filterCompaniesByType,
  formatCompanyDisplayLabel,
  getCompanyDisplayName,
  getCompanyType,
  isPrivateCompanyIdentifier,
  matchesCompanySearch,
} from '../../utils/companyHelpers';
import {
  productOwnerLabel,
  productPath,
  searchProducts,
  useProductIndex,
} from '../../utils/productHelpers';
import { FEATURE_FLAGS } from '../../pages/industry-metrics/IndustryMetrics';
import { getDevFlag } from '../../constants/devFlags';
import { marketingBaseUrl } from '../../config/siteUrls';
import {
  INDUSTRY_DATASET_IDS,
  buildIndustryDatasetPath,
} from '../../configs/industryDatasetState';
import {
  HIRING_DEPTHS,
  HIRING_VIEWS,
  HIRING_DEPTH_STORAGE_KEY,
  HIRING_DEPTH_VIEW_STORAGE_KEY,
  HIRING_LEGACY_VIEW_STORAGE_KEY,
  buildHiringWorkspacePath,
  getLegacyHiringView,
  normalizeHiringWorkspaceNav,
} from '../../features/hiring/hiringNavigation';
import {
  DEVELOPER_WORKSPACE_LAYERS,
  DEVELOPER_WORKSPACE_VIEWS,
  buildDeveloperWorkspacePath,
  buildLegacyDeveloperRedirectPath,
} from '../../pages/industry-metrics/developerWorkspaceConfig';
import {
  OPERATING_CATEGORY_VIEWS,
  OPERATING_METRICS_DEPTHS,
  buildOperatingMetricsPath,
} from '../../pages/industry-metrics/subpages/operatingMetricsNavigation';
import {
  MARKET_COVERAGE_SECTIONS,
  MARKET_COVERAGE_VIEWS,
  buildMarketCoveragePath,
} from '../../pages/market-insights/subpages/marketCoverageWorkspaceConfig';
import {
  SI_HIRING_SECTIONS,
  SI_HIRING_VIEWS,
  buildSIHiringPath,
} from '../../pages/market-insights/subpages/siHiringWorkspaceConfig';
import {
  GUIDANCE_DEPTHS,
  GUIDANCE_VIEWS,
  buildGuidanceWorkspacePath,
} from '../../features/guidance/guidanceNavigation';
import {
  FILING_CHANGES_DEPTHS,
  FILING_CHANGES_VIEWS,
  buildFilingChangesPath,
} from '../../features/filing-changes/filingChangesNavigation';

const SHOW_EXCHANGE_VOLUME = getDevFlag('EXCHANGE_VOLUME');
const SHOW_SI_HIRING = getDevFlag('SI_HIRING');
const SHOW_GUIDANCE = getDevFlag('GUIDANCE_PREVIEW');
const SHOW_FILING_CHANGES = getDevFlag('FILING_INTELLIGENCE_PREVIEW');
const SHOW_FULL_ACQUISITIONS = getDevFlag('ACQUISITIONS_FULL');
const SHOW_DEVELOPER_LEADERBOARD = getDevFlag('DEVELOPER_SIGNALS_LEADERBOARD');
const MARKET_INSIGHTS_SI_HIRING_VIEW_STORAGE_KEY = 'marketInsightsSIHiringView';
const MIN_COMPANY_SEARCH_CHARS = 2;
const MAX_RECENT_SEARCHES = 5;
const RECENT_SEARCHES_STORAGE_KEY = 'recentSearches';
const RECENT_SEARCHES_CHANGE_EVENT = 'recentSearchesChange';
const NO_COMPANY_RESULTS_OPTION = {
  companyName: 'No results found',
  ticker: '__no_company_results__',
  searchGroup: 'results',
  isNoResults: true,
};

const readRecentSearches = () => {
  try {
    const value = JSON.parse(localStorage.getItem(RECENT_SEARCHES_STORAGE_KEY) || '[]');
    return Array.isArray(value) ? value.flatMap((term) => {
      if (!isPrivateCompanyIdentifier(term)) return [term];
      const name = getCompanyDisplayName(term);
      return name === 'Private company' ? [] : [name];
    }) : [];
  } catch {
    return [];
  }
};

const buildCompanySearchOptions = (options, recentOptions, query, products = []) => {
  const normalizedQuery = query.trim().toLowerCase();
  const searching = normalizedQuery.length >= MIN_COMPANY_SEARCH_CHARS;
  const matches = searching
    ? options.filter((option) => matchesCompanySearch(option, normalizedQuery))
    : [];
  // Dev-only (PRODUCTS flag): products are searchable next to companies.
  const productMatches = searching ? searchProducts(products, normalizedQuery) : [];
  const matchTickers = new Set(matches.map((option) => option.ticker));
  const recents = recentOptions.filter((option) => !matchTickers.has(option.ticker));

  return [
    ...(searching && matches.length === 0 && productMatches.length === 0
      ? [NO_COMPANY_RESULTS_OPTION]
      : matches.map((option) => ({ ...option, searchGroup: 'results' }))),
    ...productMatches.map((product) => ({
      ...product, entityKind: 'product', ticker: `product:${product.productKey}`, searchGroup: 'products',
    })),
    ...recents.map((option) => ({ ...option, searchGroup: 'recent' })),
  ];
};

const SEARCH_GROUP_LABELS = { results: 'Search Results', products: 'Products', recent: 'Recent Searches' };

const renderCompanySearchGroup = (params) => (
  <li key={params.key}>
    {SEARCH_GROUP_LABELS[params.group] && (
      <Box
        sx={{
          px: 1.25,
          py: 0.45,
          fontWeight: 700,
          fontSize: '10px',
          textTransform: 'uppercase',
          letterSpacing: '0.3px',
          color: 'text.secondary',
          borderTop: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        {SEARCH_GROUP_LABELS[params.group]}
      </Box>
    )}
    <Box component="ul" sx={{ p: 0, m: 0 }}>
      {params.children}
    </Box>
  </li>
);

// ============================================================================
// Style Objects
// ============================================================================

// Theme-aware highlight colors derived from the shared brand palette.
const getHighlightColor = (theme, opacity = 0.5) => {
  return theme.palette.mode === 'light'
    ? `rgba(41, 82, 163, ${opacity * 0.2})`
    : `rgba(124, 167, 255, ${opacity * 0.24})`;
};

const getDropdownShellColor = (theme) => (
  theme.chrome?.menuSurface || theme.palette.background.paper
);

const getDropdownSurfaceColor = (theme) => (
  theme.chrome?.menuContentSurface || theme.palette.background.default
);

const navButtonBaseSx = {
  textTransform: 'none',
  fontSize: '14px',
  height: 'var(--siq-header-nav-control-height)',
  px: 'var(--siq-header-nav-control-padding-inline)',
  minWidth: 'auto',
  transition: 'color 0.2s ease, background-color 0.2s ease',
};

const getNavButtonSx = (theme, isActive, hasDropdown = false, dropdownActive = false) => ({
  ...navButtonBaseSx,
  fontFamily: theme.chrome?.fontFamily,
  color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
  backgroundColor: dropdownActive ? getHighlightColor(theme, 0.5) : 'transparent',
  '&:hover': {
    backgroundColor: getHighlightColor(theme, 0.5),
    color: theme.palette.text.primary,
  },
});

const dropdownPaperSx = (theme) => ({
  width: 'var(--siq-dataset-menu-width)',
  height: 'auto',
  backgroundColor: getDropdownShellColor(theme),
  backdropFilter: 'blur(18px)',
  border: `1px solid ${theme.chrome?.headerBorder || theme.palette.divider}`,
  borderRadius: 'var(--siq-dataset-menu-radius)',
  boxShadow: theme.chrome?.menuShadow,
  fontFamily: theme.chrome?.fontFamily,
  mt: 'var(--siq-dataset-menu-offset)',
  overflow: 'hidden',
});

const mobileNavigationLinkSx = (theme) => ({
  justifyContent: 'flex-start',
  width: '100%',
  minHeight: 48,
  px: 2,
  color: theme.palette.text.primary,
  fontSize: '16px',
  fontWeight: 700,
  textTransform: 'none',
  borderRadius: 0,
  borderBottom: `1px solid ${theme.palette.divider}`,
});

const menuSectionTitleSx = (theme) => ({
  display: 'block',
  fontWeight: 600,
  fontSize: '10px',
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
  mb: 0.75,
  pb: 0.5,
  color: theme.palette.text.secondary,
  borderBottom: `1px solid ${theme.palette.divider}`,
});

const menuPreviewLayoutSx = (theme) => ({
  display: 'grid',
  gridTemplateColumns: '255px minmax(0, 1fr)',
  gap: 1.25,
  p: 1.5,
  height: '100%',
  minHeight: 0,
  backgroundColor: getDropdownSurfaceColor(theme),
});

const menuListPanelSx = (theme) => ({
  p: 0.75,
  height: '100%',
  minHeight: 0,
  borderRadius: '10px',
  backgroundColor: getHighlightColor(theme, 0.35),
});

const menuDetailPanelSx = (theme) => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  minHeight: 0,
  height: '100%',
  p: 1.5,
  borderRadius: '10px',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: getDropdownShellColor(theme),
});

const menuListButtonSx = (theme, active = false) => ({
  width: '100%',
  display: 'grid',
  gridTemplateColumns: '34px minmax(0, 1fr)',
  alignItems: 'center',
  gap: 1,
  p: 1,
  border: `1px solid ${active ? theme.palette.primary.main : 'transparent'}`,
  borderRadius: 1.25,
  color: theme.palette.text.primary,
  backgroundColor: active
    ? getHighlightColor(theme, 0.65)
    : 'transparent',
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'background-color 0.16s ease, border-color 0.16s ease',
  '&:hover, &:focus-visible': {
    backgroundColor: getHighlightColor(theme, 0.55),
    borderColor: active ? theme.palette.primary.main : theme.palette.divider,
    outline: 'none',
  },
});

const menuShortcutButtonSx = (theme, active = false, hasDescription = false) => ({
  flex: hasDescription ? '1 1 145px' : '0 0 auto',
  minHeight: hasDescription ? 44 : 30,
  justifyContent: 'flex-start',
  textTransform: 'none',
  fontSize: '12px',
  fontWeight: active ? 700 : 600,
  px: 1,
  py: hasDescription ? 0.65 : 0.4,
  borderRadius: 1,
  color: active ? theme.palette.primary.contrastText : theme.palette.text.secondary,
  backgroundColor: active ? theme.palette.primary.main : 'transparent',
  border: `1px solid ${active ? theme.palette.primary.main : theme.palette.divider}`,
  textAlign: 'left',
  alignItems: hasDescription ? 'flex-start' : 'center',
  '& .MuiButton-startIcon': {
    mt: hasDescription ? 0.15 : 0,
    mr: 0.75,
    ml: 0,
  },
  '&:hover': {
    backgroundColor: active ? theme.palette.primary.dark : getHighlightColor(theme, 0.45),
    borderColor: theme.palette.primary.main,
    color: active ? theme.palette.primary.contrastText : theme.palette.text.primary,
  },
});

const mobileMenuPrimaryButtonSx = (theme) => ({
  justifyContent: 'center',
  minHeight: 42,
  textTransform: 'none',
  fontSize: '13px',
  fontWeight: 800,
  px: 1.5,
  py: 0.85,
  borderRadius: 1,
  color: theme.palette.primary.contrastText,
  backgroundColor: theme.palette.primary.main,
  border: `1px solid ${theme.palette.primary.main}`,
  boxShadow: theme.palette.mode === 'light'
    ? '0 8px 18px rgba(63, 100, 168, 0.24)'
    : '0 8px 18px rgba(145, 166, 205, 0.18)',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    borderColor: theme.palette.primary.dark,
  },
});

const accordionSx = (theme) => ({
  color: theme.palette.primary.main,
  backgroundColor: theme.background?.transparent || 'transparent',
  '&:before': { display: 'none' },
  boxShadow: 'none',
});

const accordionSummarySx = (theme) => ({
  backgroundColor: theme.background?.transparent || 'transparent',
  minHeight: 48,
  '&.Mui-expanded': { minHeight: 48 },
  '& .MuiAccordionSummary-content': { margin: '12px 0' },
});

const searchWrapperSx = {
  display: 'flex',
  alignItems: 'center',
};

// ============================================================================
// Header Component
// ============================================================================

function Header({ themeMode, toggleTheme }) {
  const navigate = useNavigate();
  const location = useLocation();
  const baseTheme = useTheme();
  const theme = useMemo(() => isDocumentationRoute(location.pathname) ? getDocumentationTheme(baseTheme) : baseTheme, [baseTheme, location.pathname]);
  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();


  // Responsive breakpoints
  const isDesktop = useMediaQuery('(max-width: 900px)');

  // ============================================================================
  // State Management - Unified dropdown state
  // ============================================================================
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null); // 'metrics' | 'insights'
  const [activeMenuItemId, setActiveMenuItemId] = useState(null);
  const dropdownAnchorRef = useRef(null);

  // Mobile nav state
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [accordionOpen, setAccordionOpen] = useState({
    industryMetrics: false,
    benchmarks: false,
  });

  // Search state
  const [companyQuery, setCompanyQuery] = useState('');
  const [inlineSearchOpen, setInlineSearchOpen] = useState(false);
  const [inlineSearchFocused, setInlineSearchFocused] = useState(false);
  const searchInputRef = useRef(null);

  // Mobile search state
  const [mobileQuery, setMobileQuery] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedCompanyTypes, setSelectedCompanyTypes] = useState(
    () => COMPANY_TYPE_FILTERS.map((type) => type.id)
  );
  const [recentSearches, setRecentSearches] = useState(readRecentSearches);
  const { products: productIndex } = useProductIndex();

  // Company data
  const itemList = useMemo(
    () => itemListData.map((it) => ({ ...it, companyType: getCompanyType(it) })),
    []
  );
  const companyOptions = useMemo(
    () => itemList.map((it) => ({ companyName: it.name, ticker: it.ticker, companyType: it.companyType })),
    [itemList]
  );
  const filteredCompanyOptions = useMemo(
    () => filterCompaniesByType(companyOptions, selectedCompanyTypes),
    [companyOptions, selectedCompanyTypes]
  );
  const recentCompanyOptions = useMemo(
    () => recentSearches
      .map((term) => filteredCompanyOptions.find(
        (option) => option.companyName === term || option.ticker === term
      ))
      .filter(Boolean),
    [filteredCompanyOptions, recentSearches]
  );
  const mobileRecentItems = useMemo(
    () => recentSearches
      .map((term) => itemList.find((item) => item.name === term || item.ticker === term))
      .filter((item) => item && selectedCompanyTypes.includes(item.companyType))
      .filter((item) => !filteredItems.some((filteredItem) => filteredItem.ticker === item.ticker))
      .filter(Boolean),
    [filteredItems, itemList, recentSearches, selectedCompanyTypes]
  );

  // ============================================================================
  // Active State Detection
  // ============================================================================
  const isActiveSection = (section) => {
    const path = location.pathname;
    switch (section) {
      case 'about':
        return path === '/about';
      case 'metrics':
        return path.startsWith('/kpis') ||
          path.startsWith('/developer') ||
          path.startsWith('/techstack') ||
          path.startsWith('/hiring') ||
          path.startsWith('/sihiring') ||
          path.startsWith('/benchmarks') ||
          (SHOW_GUIDANCE && path.startsWith('/guidance'));
      case 'benchmarks':
        return path.startsWith('/marketinsights');
      case 'company':
        return path.startsWith('/company');
      default:
        return false;
    }
  };

  // ============================================================================
  // Unified Dropdown Handlers
  // ============================================================================
  const handleNavButtonClick = (section) => () => {
    setDropdownOpen((wasOpen) => {
      const nextOpen = activeSection !== section || !wasOpen;
      setActiveSection(nextOpen ? section : null);
      return nextOpen;
    });
  };

  const closeAllMenus = () => {
    setDropdownOpen(false);
    setActiveSection(null);
    setMobileNavOpen(false);
    setAccordionOpen({ industryMetrics: false, benchmarks: false });
    setSearchOpen(false);
  };

  const toggleAccordion = (panel) => () => {
    setAccordionOpen((prev) => ({ ...prev, [panel]: !prev[panel] }));
  };

  useEffect(() => {
    setDropdownOpen(false);
    setActiveSection(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!dropdownOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setDropdownOpen(false);
        setActiveSection(null);
        dropdownAnchorRef.current?.querySelector('button[aria-haspopup]')?.focus();
      }
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [dropdownOpen]);

  useEffect(() => {
    const syncRecentSearches = () => setRecentSearches(readRecentSearches());
    const handleStorage = (event) => {
      if (event.key === RECENT_SEARCHES_STORAGE_KEY) {
        syncRecentSearches();
      }
    };

    window.addEventListener(RECENT_SEARCHES_CHANGE_EVENT, syncRecentSearches);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(RECENT_SEARCHES_CHANGE_EVENT, syncRecentSearches);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const updateRecentSearches = (searchTerm) => {
    if (!searchTerm) return;
    const current = readRecentSearches();
    const updated = [searchTerm, ...current.filter((item) => item !== searchTerm)].slice(0, MAX_RECENT_SEARCHES);
    localStorage.setItem(RECENT_SEARCHES_STORAGE_KEY, JSON.stringify(updated));
    setRecentSearches(updated);
    window.dispatchEvent(new CustomEvent(RECENT_SEARCHES_CHANGE_EVENT));
  };

  // ============================================================================
  // Navigation Handlers
  // ============================================================================
  const goToCompany = (vOrString) => {
    const t = typeof vOrString === 'string' ? vOrString : vOrString?.ticker;
    if (!t) return;
    const selected = typeof vOrString === 'string'
      ? companyOptions.find((option) => option.ticker.toLowerCase() === t.toLowerCase())
      : vOrString;
    if (selected?.companyName) {
      updateRecentSearches(selected.companyName);
    }
    navigate(`/company/${t.toLowerCase()}/keymetrics`);
  };

  const getOperatingMetricsNavPath = (depth, view) => (
    buildIndustryDatasetPath({
      targetDatasetId: INDUSTRY_DATASET_IDS.OPERATING_KPIS,
      activeLayerId: depth,
      viewId: view,
    }) || buildOperatingMetricsPath({ depth, view })
  );

  const getHiringNavPath = (depth, view) => {
    const nav = normalizeHiringWorkspaceNav({ depth, view });
    return buildIndustryDatasetPath({
      targetDatasetId: INDUSTRY_DATASET_IDS.JOB_POSTINGS,
      activeLayerId: nav.depth,
      viewId: nav.view,
    }) || buildHiringWorkspacePath(nav);
  };

  const getDeveloperNavPath = (layer, view) => (
    buildIndustryDatasetPath({
      targetDatasetId: INDUSTRY_DATASET_IDS.DEVELOPER_SIGNALS,
      activeLayerId: layer,
      viewId: view,
    }) || buildDeveloperWorkspacePath({ layer, view })
  );

  const getSIHiringNavPath = (sectionId, viewId = SI_HIRING_VIEWS.TRENDS) => (
    buildIndustryDatasetPath({
      targetDatasetId: INDUSTRY_DATASET_IDS.PARTNER_TRENDS,
      activeLayerId: sectionId,
      viewId,
    }) || buildSIHiringPath({ sectionId, viewId })
  );

  const getGuidanceNavPath = (depth, view = GUIDANCE_VIEWS.RESULTS) => (
    buildIndustryDatasetPath({
      targetDatasetId: INDUSTRY_DATASET_IDS.GUIDANCE,
      activeLayerId: depth,
      viewId: view,
    }) || buildGuidanceWorkspacePath({ depth, view })
  );

  const getFilingChangesNavPath = (depth, view = FILING_CHANGES_VIEWS.EVENTS) => (
    buildIndustryDatasetPath({
      targetDatasetId: INDUSTRY_DATASET_IDS.FILING_CHANGES,
      activeLayerId: depth,
      viewId: view,
    }) || buildFilingChangesPath({ sectionId: depth, viewId: view })
  );

  // Navigate to metrics page with specific view
  const navigateToMetricsView = (view) => () => {
    localStorage.setItem('industryMetricsView', view);
    localStorage.setItem('lastIndustryMetricsPath', 'metrics');
    // Dispatch custom event for same-page updates
    window.dispatchEvent(new CustomEvent('metricsViewChange', { detail: { view } }));
    navigate(view === 'trended'
      ? getOperatingMetricsNavPath(OPERATING_METRICS_DEPTHS.CATEGORY, OPERATING_CATEGORY_VIEWS.DISTRIBUTION)
      : getOperatingMetricsNavPath(OPERATING_METRICS_DEPTHS.VENDOR, view));
    closeAllMenus();
  };

  // Get current metrics view from localStorage for highlighting
  const getCurrentMetricsView = () => {
    return localStorage.getItem('industryMetricsView') || 'table';
  };

  // Check if a specific metrics view is active
  const isMetricsViewActive = (view) => {
    if (view === 'trended') {
      return location.pathname === '/kpis/category/trended';
    }

    return (
      location.pathname === `/kpis/vendor/${view}` ||
      (location.pathname === '/kpis' && getCurrentMetricsView() === view)
    );
  };

  const navigateToOperatingMetricsNav = (depth, view) => () => {
    localStorage.setItem('industryMetricsView', view);
    localStorage.setItem('lastIndustryMetricsPath', 'metrics');
    window.dispatchEvent(new CustomEvent('metricsViewChange', { detail: { view } }));
    navigate(getOperatingMetricsNavPath(depth, view));
    closeAllMenus();
  };

  const isOperatingMetricsNavActive = (depth, view) => (
    location.pathname === buildOperatingMetricsPath({ depth, view })
  );

  const navigateToHiringNav = (depth, view) => () => {
    const nav = normalizeHiringWorkspaceNav({ depth, view });
    const legacyView = getLegacyHiringView(nav.depth, nav.view);
    localStorage.setItem(HIRING_DEPTH_STORAGE_KEY, nav.depth);
    localStorage.setItem(HIRING_DEPTH_VIEW_STORAGE_KEY, nav.view);
    localStorage.setItem(HIRING_LEGACY_VIEW_STORAGE_KEY, legacyView);
    navigate(getHiringNavPath(nav.depth, nav.view));
    closeAllMenus();
  };

  const getCurrentHiringNav = () => {
    return normalizeHiringWorkspaceNav({
      depth: localStorage.getItem(HIRING_DEPTH_STORAGE_KEY),
      view: localStorage.getItem(HIRING_DEPTH_VIEW_STORAGE_KEY),
      legacyView: localStorage.getItem(HIRING_LEGACY_VIEW_STORAGE_KEY),
    });
  };

  const isHiringNavActive = (depth, view) => {
    const current = getCurrentHiringNav();
    const nav = normalizeHiringWorkspaceNav({ depth, view });
    return location.pathname.startsWith('/hiring/') &&
      current.depth === nav.depth &&
      current.view === nav.view;
  };

  // Navigate to developer metrics page with specific view
  const navigateToDevView = (view) => () => {
    localStorage.setItem('developerMetricsView', view);
    localStorage.setItem('lastIndustryMetricsPath', 'developer');
    window.dispatchEvent(new CustomEvent('devViewChange', { detail: { view } }));
    navigate(buildLegacyDeveloperRedirectPath({ view }));
    closeAllMenus();
  };

  // Get current dev view from localStorage for highlighting
  const getCurrentDevView = () => {
    return localStorage.getItem('developerMetricsView') || 'overview';
  };

  // Check if a specific dev view is active
  const isDevViewActive = (view) => {
    const targetPath = buildLegacyDeveloperRedirectPath({ view }).split('?')[0];
    return (
      location.pathname === targetPath ||
      (location.pathname === '/developer' && getCurrentDevView() === view)
    );
  };

  const getLegacyDeveloperViewForNav = (layer, view) => {
    if (layer === DEVELOPER_WORKSPACE_LAYERS.PROJECT_DETAIL) return 'detail';
    if (view === DEVELOPER_WORKSPACE_VIEWS.LEADERBOARD) return 'leaderboard';
    return 'overview';
  };

  const navigateToDeveloperNav = (layer, view) => () => {
    const legacyView = getLegacyDeveloperViewForNav(layer, view);
    localStorage.setItem('developerMetricsView', legacyView);
    localStorage.setItem('lastIndustryMetricsPath', 'developer');
    window.dispatchEvent(new CustomEvent('devViewChange', { detail: { view: legacyView } }));
    navigate(getDeveloperNavPath(layer, view));
    closeAllMenus();
  };

  const isDeveloperNavActive = (layer, view) => (
    location.pathname === buildDeveloperWorkspacePath({ layer, view })
  );

  // Navigate to market insights coverage with specific view
  const navigateToMarketInsightsCoverageView = (view) => () => {
    localStorage.setItem('marketInsightsCoverageView', view);
    localStorage.setItem('lastMarketInsightsPath', 'coverage');
    window.dispatchEvent(new CustomEvent('marketInsightsCoverageViewChange', { detail: { view } }));
    navigate('/marketinsights/coverage');
    closeAllMenus();
  };

  // Check if a specific coverage view is active
  const isMarketInsightsCoverageViewActive = (view) => {
    return location.pathname === '/marketinsights/coverage' &&
      localStorage.getItem('marketInsightsCoverageView') === view;
  };

  const navigateToMarketCoverageNav = (sectionId, viewId) => () => {
    const legacyView = 'market';
    localStorage.setItem('marketInsightsCoverageView', legacyView);
    localStorage.setItem('lastMarketInsightsPath', sectionId === MARKET_COVERAGE_SECTIONS.COVERAGE ? 'coverage' : sectionId === MARKET_COVERAGE_SECTIONS.ACQUISITIONS ? 'acquisitions' : 'ventureBacked');
    if (sectionId === MARKET_COVERAGE_SECTIONS.ACQUISITIONS) {
      localStorage.setItem('marketInsightsAcquisitionsView', viewId);
      window.dispatchEvent(new CustomEvent('marketInsightsAcquisitionsViewChange', { detail: { view: viewId } }));
    } else {
      window.dispatchEvent(new CustomEvent('marketInsightsCoverageViewChange', { detail: { view: legacyView } }));
    }
    navigate(buildMarketCoveragePath({ sectionId, viewId }));
    closeAllMenus();
  };

  const isMarketCoverageNavActive = (sectionId, viewId) => (
    location.pathname === buildMarketCoveragePath({ sectionId, viewId })
  );

  const navigateToMarketInsightsAcquisitions = () => {
    localStorage.setItem('marketInsightsAcquisitionsView', 'public');
    localStorage.setItem('lastMarketInsightsPath', 'acquisitions');
    window.dispatchEvent(new CustomEvent('marketInsightsAcquisitionsViewChange', { detail: { view: 'public' } }));
    navigate('/marketinsights/acquisitions');
    closeAllMenus();
  };

  const navigateToVentureBacked = () => {
    localStorage.setItem('lastMarketInsightsPath', 'ventureBacked');
    navigate('/marketinsights/venture-backed');
    closeAllMenus();
  };

  // Navigate to market insights exchange volume with specific view
  const navigateToExchangeVolumeView = (view) => () => {
    localStorage.setItem('marketInsightsExchangeVolumeView', view);
    localStorage.setItem('lastMarketInsightsPath', 'exchangevolume');
    window.dispatchEvent(new CustomEvent('marketInsightsExchangeVolumeViewChange', { detail: { view } }));
    navigate('/marketinsights/exchangevolume');
    closeAllMenus();
  };

  // Check if a specific exchange volume view is active
  const isExchangeVolumeViewActive = (view) => {
    return location.pathname === '/marketinsights/exchangevolume' &&
      localStorage.getItem('marketInsightsExchangeVolumeView') === view;
  };

  // Navigate to market insights SI hiring with specific view
  const normalizeSIHiringView = (view) => ({
    dashboard: 'vendor',
  }[view] || view || 'vendor');

  const getSIHiringPath = (view) => {
    const normalizedView = normalizeSIHiringView(view);
    if (normalizedView === 'detail') return getSIHiringNavPath(SI_HIRING_SECTIONS.COMPANY, SI_HIRING_VIEWS.DASHBOARD);
    if (normalizedView === 'category') return getSIHiringNavPath(SI_HIRING_SECTIONS.CATEGORY, SI_HIRING_VIEWS.TRENDS);
    if (normalizedView === 'market') return getSIHiringNavPath(SI_HIRING_SECTIONS.MARKET, SI_HIRING_VIEWS.TRENDS);
    return getSIHiringNavPath(SI_HIRING_SECTIONS.VENDOR, SI_HIRING_VIEWS.TRENDS);
  };

  const navigateToSIHiringView = (view) => () => {
    const normalizedView = normalizeSIHiringView(view);
    localStorage.setItem(MARKET_INSIGHTS_SI_HIRING_VIEW_STORAGE_KEY, normalizedView);
    localStorage.setItem('lastMarketInsightsPath', 'sihiring');
    window.dispatchEvent(new CustomEvent('marketInsightsSIHiringViewChange', { detail: { view: normalizedView } }));
    navigate(getSIHiringPath(normalizedView));
    closeAllMenus();
  };

  // Check if a specific SI hiring view is active
  const isSIHiringViewActive = (view) => {
    const activeView = normalizeSIHiringView(localStorage.getItem(MARKET_INSIGHTS_SI_HIRING_VIEW_STORAGE_KEY) || 'vendor');
    const normalizedView = normalizeSIHiringView(view);
    if (location.pathname.startsWith('/sihiring/company')) return normalizedView === 'detail';
    if (location.pathname.startsWith('/sihiring/category')) return normalizedView === 'category';
    if (location.pathname.startsWith('/sihiring/market')) return normalizedView === 'market';
    if (location.pathname.startsWith('/sihiring/vendor')) return normalizedView === 'vendor';
    return activeView === normalizedView;
  };

  const navigateToSIHiringNav = (sectionId, viewId = SI_HIRING_VIEWS.TRENDS) => () => {
    const storageView = sectionId === SI_HIRING_SECTIONS.COMPANY
      ? 'detail'
      : sectionId;
    localStorage.setItem(MARKET_INSIGHTS_SI_HIRING_VIEW_STORAGE_KEY, storageView);
    localStorage.setItem('lastMarketInsightsPath', 'sihiring');
    window.dispatchEvent(new CustomEvent('marketInsightsSIHiringViewChange', { detail: { view: storageView } }));
    navigate(getSIHiringNavPath(sectionId, viewId));
    closeAllMenus();
  };

  const isSIHiringNavActive = (sectionId, viewId = SI_HIRING_VIEWS.TRENDS) => (
    location.pathname === buildSIHiringPath({ sectionId, viewId })
  );

  const navigateToGuidanceNav = (depth, view = GUIDANCE_VIEWS.RESULTS) => () => {
    navigate(getGuidanceNavPath(depth, view));
    closeAllMenus();
  };

  const isGuidanceNavActive = (depth, view = GUIDANCE_VIEWS.RESULTS) => (
    location.pathname === buildGuidanceWorkspacePath({ depth, view })
  );

  const navigateToFilingChangesNav = (depth, view = FILING_CHANGES_VIEWS.EVENTS) => () => {
    navigate(getFilingChangesNavPath(depth, view));
    closeAllMenus();
  };

  const isFilingChangesNavActive = (depth, view = FILING_CHANGES_VIEWS.EVENTS) => (
    location.pathname === buildFilingChangesPath({ sectionId: depth, viewId: view })
  );

  // ============================================================================
  // Search Handlers
  // ============================================================================
  useEffect(() => {
    if (mobileQuery.length >= 2) {
      const query = mobileQuery.toLowerCase();
      const filtered = itemList.filter((item) =>
        selectedCompanyTypes.includes(item.companyType) &&
        matchesCompanySearch({ companyName: item.name, ticker: item.ticker }, query)
      );
      setFilteredItems(filtered);
    } else {
      setFilteredItems([]);
    }
  }, [itemList, mobileQuery, selectedCompanyTypes]);

  const handleMobileSearchChange = (event) => {
    setMobileQuery(event.target.value);
  };

  const handleSearchSelect = (searchTerm) => {
    trackEvent('search', { search_term: searchTerm, result_count: filteredItems.length });
    updateRecentSearches(searchTerm);
    window.scrollTo(0, 0);
    setMobileQuery('');
    setFilteredItems([]);
  };

  const generateLinks = (items) => {
    return items.map((item, index) => (
      <CompanySearchOptionRow
        key={item.ticker || index}
        option={{ companyName: item.name, ticker: item.ticker, companyType: item.companyType }}
        expandable
        onNavigate={(ticker, destination) => {
          handleSearchSelect(item.name);
          closeAllMenus();
          setFilteredItems([]);
          navigate(`/company/${ticker.toLowerCase()}/${destination}`);
        }}
        theme={theme}
      />
    ));
  };

  // ============================================================================
  // Login Button Component
  // ============================================================================
  const LoginButton = ({ mobile = false }) => {
    const handleLoginClick = () => {
      const returnTo = window.location.pathname;
      localStorage.setItem('redirectTo', returnTo);
      loginWithRedirect();
    };

    return (
      <Button
        sx={getHeaderSignInSx(theme, mobile)}
        onClick={handleLoginClick}
      >
        Sign In
      </Button>
    );
  };

  // ============================================================================
  // Dropdown Content Components
  // ============================================================================
  const renderIcon = (icon, size = 19) => React.cloneElement(icon, { sx: { fontSize: size } });

  const buildShortcut = ({
    label,
    description,
    icon,
    onClick,
    active,
  }) => ({
    label,
    description,
    icon,
    onClick,
    active,
  });

  // Preserved temporarily while preview datasets await entries in the public navigation contract.
  // eslint-disable-next-line no-unused-vars
  const datasetMenuItems = [
    {
      id: 'operating-kpis',
      label: 'Operating KPIs',
      subtitle: 'Financials, KPIs, comps',
      description: 'Compare public software company fundamentals by market, vendor, and company.',
      icon: <AssessmentIcon />,
      chips: ['Core', 'Financials', 'KPIs'],
      compactShortcuts: true,
      defaultAction: navigateToOperatingMetricsNav(OPERATING_METRICS_DEPTHS.VENDOR, 'table'),
      active: location.pathname.startsWith('/kpis'),
      shortcutGroups: [
        {
          label: 'Market',
          icon: <PublicIcon />,
          shortcuts: [
            buildShortcut({ label: 'Dashboard', description: 'Aggregate KPI medians', icon: <BarChartIcon />, onClick: navigateToOperatingMetricsNav(OPERATING_METRICS_DEPTHS.CATEGORY, OPERATING_CATEGORY_VIEWS.TRENDS), active: isOperatingMetricsNavActive(OPERATING_METRICS_DEPTHS.CATEGORY, OPERATING_CATEGORY_VIEWS.TRENDS) }),
            buildShortcut({ label: 'Valuation', description: 'Category valuation ranges', icon: <ScatterPlotIcon />, onClick: navigateToOperatingMetricsNav(OPERATING_METRICS_DEPTHS.CATEGORY, OPERATING_CATEGORY_VIEWS.VALUATION), active: isOperatingMetricsNavActive(OPERATING_METRICS_DEPTHS.CATEGORY, OPERATING_CATEGORY_VIEWS.VALUATION) }),
            buildShortcut({ label: 'Distribution', description: 'Metric distributions by sector', icon: <CandlestickChartIcon />, onClick: navigateToOperatingMetricsNav(OPERATING_METRICS_DEPTHS.CATEGORY, OPERATING_CATEGORY_VIEWS.DISTRIBUTION), active: isOperatingMetricsNavActive(OPERATING_METRICS_DEPTHS.CATEGORY, OPERATING_CATEGORY_VIEWS.DISTRIBUTION) }),
          ],
        },
        {
          label: 'Vendor',
          icon: <InsightsIcon />,
          shortcuts: [
            buildShortcut({ label: 'Comp Sheet', description: 'Screen company fundamentals', icon: <GridViewIcon />, onClick: navigateToOperatingMetricsNav(OPERATING_METRICS_DEPTHS.VENDOR, 'table'), active: isOperatingMetricsNavActive(OPERATING_METRICS_DEPTHS.VENDOR, 'table') }),
            buildShortcut({ label: 'Current Metrics', description: 'KPI, valuation, acceleration, and efficiency views', icon: <BarChartIcon />, onClick: navigateToOperatingMetricsNav(OPERATING_METRICS_DEPTHS.VENDOR, 'chart'), active: ['chart', 'scatter', 'acceleration', 'quadrant'].some((view) => isOperatingMetricsNavActive(OPERATING_METRICS_DEPTHS.VENDOR, view)) }),
          ],
        },
        {
          label: 'Company Detail',
          icon: <BusinessIcon />,
          shortcuts: [
            buildShortcut({ label: 'Dashboard', description: 'Financial dashboard', icon: <BusinessIcon />, onClick: navigateToOperatingMetricsNav(OPERATING_METRICS_DEPTHS.COMPANY, 'dashboard'), active: isOperatingMetricsNavActive(OPERATING_METRICS_DEPTHS.COMPANY, 'dashboard') }),
            buildShortcut({ label: 'Trajectory', description: 'Growth trajectory', icon: <TrendingUpIcon />, onClick: navigateToOperatingMetricsNav(OPERATING_METRICS_DEPTHS.COMPANY, 'trajectory'), active: isOperatingMetricsNavActive(OPERATING_METRICS_DEPTHS.COMPANY, 'trajectory') }),
            buildShortcut({ label: 'Margins', description: 'Profitability and margins', icon: <BarChartIcon />, onClick: navigateToOperatingMetricsNav(OPERATING_METRICS_DEPTHS.COMPANY, 'margins'), active: isOperatingMetricsNavActive(OPERATING_METRICS_DEPTHS.COMPANY, 'margins') }),
            buildShortcut({ label: 'Model', description: 'Financial model', icon: <GridOnIcon />, onClick: navigateToOperatingMetricsNav(OPERATING_METRICS_DEPTHS.COMPANY, 'model'), active: isOperatingMetricsNavActive(OPERATING_METRICS_DEPTHS.COMPANY, 'model') }),
          ],
        },
      ],
    },
    {
      id: 'job-postings',
      label: 'Job Postings',
      subtitle: 'Hiring demand and roles',
      description: 'Navigate weekly hiring signals by market, vendor, company, role mix, and active postings.',
      icon: <WorkOutlineIcon />,
      chips: ['Alt Data', 'Weekly', 'Hiring'],
      compactShortcuts: true,
      defaultAction: navigateToHiringNav(HIRING_DEPTHS.VENDOR, HIRING_VIEWS.TREND),
      active: location.pathname.startsWith('/hiring'),
      shortcutGroups: [
        {
          label: 'Market',
          icon: <PublicIcon />,
          shortcuts: [
            buildShortcut({ label: 'Dashboard', description: 'Posting momentum by category', icon: <TimelineIcon />, onClick: navigateToHiringNav(HIRING_DEPTHS.CATEGORY, HIRING_VIEWS.TREND), active: isHiringNavActive(HIRING_DEPTHS.CATEGORY, HIRING_VIEWS.TREND) }),
            buildShortcut({ label: 'Leadership', description: 'Executive job postings', icon: <WorkIcon />, onClick: navigateToHiringNav(HIRING_DEPTHS.CATEGORY, HIRING_VIEWS.LEADERSHIP), active: isHiringNavActive(HIRING_DEPTHS.CATEGORY, HIRING_VIEWS.LEADERSHIP) }),
          ],
        },
        {
          label: 'Vendor',
          icon: <InsightsIcon />,
          shortcuts: [
            buildShortcut({ label: 'Dashboard', description: 'Company trends and peer signals', icon: <TimelineIcon />, onClick: navigateToHiringNav(HIRING_DEPTHS.VENDOR, HIRING_VIEWS.TREND), active: isHiringNavActive(HIRING_DEPTHS.VENDOR, HIRING_VIEWS.TREND) }),
            buildShortcut({ label: 'Roles', description: 'Role mix across postings', icon: <PieChartIcon />, onClick: navigateToHiringNav(HIRING_DEPTHS.VENDOR, HIRING_VIEWS.ROLES), active: isHiringNavActive(HIRING_DEPTHS.VENDOR, HIRING_VIEWS.ROLES) }),
          ],
        },
        {
          label: 'Company Detail',
          icon: <BusinessIcon />,
          shortcuts: [
            buildShortcut({ label: 'Dashboard', description: 'Weekly posting trend', icon: <TimelineIcon />, onClick: navigateToHiringNav(HIRING_DEPTHS.COMPANY, HIRING_VIEWS.TREND), active: isHiringNavActive(HIRING_DEPTHS.COMPANY, HIRING_VIEWS.TREND) }),
            buildShortcut({ label: 'Job Board', description: 'Active openings', icon: <ViewListIcon />, onClick: navigateToHiringNav(HIRING_DEPTHS.COMPANY, HIRING_VIEWS.JOB_BOARD), active: isHiringNavActive(HIRING_DEPTHS.COMPANY, HIRING_VIEWS.JOB_BOARD) }),
          ],
        },
      ],
    },
    FEATURE_FLAGS.SHOW_DEVELOPER_SIGNALS && {
      id: 'developer-signals',
      label: 'Adoption Signals',
      subtitle: 'Open-source adoption',
      description: 'Browse project, package, company, and market-level signals from developer ecosystems.',
      icon: <CodeIcon />,
      chips: ['Alt Data', 'Open Source', 'Projects'],
      compactShortcuts: true,
      defaultAction: navigateToDeveloperNav(DEVELOPER_WORKSPACE_LAYERS.PROJECT, DEVELOPER_WORKSPACE_VIEWS.TRENDS),
      active: location.pathname.startsWith('/adoption') || location.pathname.startsWith('/developer'),
      shortcutGroups: [
        {
          label: 'Market',
          icon: <PublicIcon />,
          shortcuts: [
            buildShortcut({ label: 'Dashboard', description: 'Market-level project trends', icon: <TimelineIcon />, onClick: navigateToDeveloperNav(DEVELOPER_WORKSPACE_LAYERS.CATEGORY, DEVELOPER_WORKSPACE_VIEWS.TRENDS), active: isDeveloperNavActive(DEVELOPER_WORKSPACE_LAYERS.CATEGORY, DEVELOPER_WORKSPACE_VIEWS.TRENDS) }),
          ],
        },
        {
          label: 'Project',
          icon: <InsightsIcon />,
          shortcuts: [
            buildShortcut({ label: 'Dashboard', description: 'Project list and selected trend', icon: <TimelineIcon />, onClick: navigateToDeveloperNav(DEVELOPER_WORKSPACE_LAYERS.PROJECT, DEVELOPER_WORKSPACE_VIEWS.TRENDS), active: isDeveloperNavActive(DEVELOPER_WORKSPACE_LAYERS.PROJECT, DEVELOPER_WORKSPACE_VIEWS.TRENDS) }),
            ...(SHOW_DEVELOPER_LEADERBOARD ? [buildShortcut({ label: 'Leaderboard', description: 'Project movers and category share', icon: <TrendingUpIcon />, onClick: navigateToDeveloperNav(DEVELOPER_WORKSPACE_LAYERS.PROJECT, DEVELOPER_WORKSPACE_VIEWS.LEADERBOARD), active: isDeveloperNavActive(DEVELOPER_WORKSPACE_LAYERS.PROJECT, DEVELOPER_WORKSPACE_VIEWS.LEADERBOARD) })] : []),
          ],
        },
        {
          label: 'Project Detail',
          icon: <CodeIcon />,
          shortcuts: [
            buildShortcut({ label: 'Project Analysis', description: 'Artifact-level trends', icon: <ViewListIcon />, onClick: navigateToDeveloperNav(DEVELOPER_WORKSPACE_LAYERS.PROJECT_DETAIL, DEVELOPER_WORKSPACE_VIEWS.PACKAGES), active: isDeveloperNavActive(DEVELOPER_WORKSPACE_LAYERS.PROJECT_DETAIL, DEVELOPER_WORKSPACE_VIEWS.PACKAGES) }),
            buildShortcut({ label: 'Versions', description: 'Release activity and version share', icon: <ViewListIcon />, onClick: navigateToDeveloperNav(DEVELOPER_WORKSPACE_LAYERS.PROJECT_DETAIL, DEVELOPER_WORKSPACE_VIEWS.VERSIONS), active: isDeveloperNavActive(DEVELOPER_WORKSPACE_LAYERS.PROJECT_DETAIL, DEVELOPER_WORKSPACE_VIEWS.VERSIONS) }),
          ],
        },
      ],
    },
    SHOW_GUIDANCE && {
      id: 'guidance',
      label: 'Guidance',
      subtitle: 'Results and revisions',
      description: 'Review management guidance results, revisions, and company-level detail.',
      icon: <FactCheckIcon />,
      badge: 'Preview',
      chips: ['Earnings', 'Guidance', 'Revisions'],
      compactShortcuts: true,
      defaultAction: navigateToGuidanceNav(GUIDANCE_DEPTHS.VENDOR, GUIDANCE_VIEWS.RESULTS),
      active: location.pathname.startsWith('/guidance'),
      shortcutGroups: [
        {
          label: 'Market',
          icon: <PublicIcon />,
          shortcuts: [
            buildShortcut({ label: 'Results', description: 'Actuals vs guidance by category', icon: <BarChartIcon />, onClick: navigateToGuidanceNav(GUIDANCE_DEPTHS.CATEGORY, GUIDANCE_VIEWS.RESULTS), active: isGuidanceNavActive(GUIDANCE_DEPTHS.CATEGORY, GUIDANCE_VIEWS.RESULTS) }),
            buildShortcut({ label: 'Revisions', description: 'FY guide revisions by category', icon: <TimelineIcon />, onClick: navigateToGuidanceNav(GUIDANCE_DEPTHS.CATEGORY, GUIDANCE_VIEWS.REVISIONS), active: isGuidanceNavActive(GUIDANCE_DEPTHS.CATEGORY, GUIDANCE_VIEWS.REVISIONS) }),
          ],
        },
        {
          label: 'Vendor',
          icon: <InsightsIcon />,
          shortcuts: [
            buildShortcut({ label: 'Results', description: 'Company result beats and misses', icon: <BarChartIcon />, onClick: navigateToGuidanceNav(GUIDANCE_DEPTHS.VENDOR, GUIDANCE_VIEWS.RESULTS), active: isGuidanceNavActive(GUIDANCE_DEPTHS.VENDOR, GUIDANCE_VIEWS.RESULTS) }),
            buildShortcut({ label: 'Revisions', description: 'Company FY guide revisions', icon: <TimelineIcon />, onClick: navigateToGuidanceNav(GUIDANCE_DEPTHS.VENDOR, GUIDANCE_VIEWS.REVISIONS), active: isGuidanceNavActive(GUIDANCE_DEPTHS.VENDOR, GUIDANCE_VIEWS.REVISIONS) }),
          ],
        },
        {
          label: 'Company Detail',
          icon: <BusinessIcon />,
          shortcuts: [
            buildShortcut({ label: 'Results', description: 'Detailed results vs guidance', icon: <BarChartIcon />, onClick: navigateToGuidanceNav(GUIDANCE_DEPTHS.COMPANY, GUIDANCE_VIEWS.RESULTS), active: isGuidanceNavActive(GUIDANCE_DEPTHS.COMPANY, GUIDANCE_VIEWS.RESULTS) }),
            buildShortcut({ label: 'Revisions', description: 'Detailed FY guide revisions', icon: <TimelineIcon />, onClick: navigateToGuidanceNav(GUIDANCE_DEPTHS.COMPANY, GUIDANCE_VIEWS.REVISIONS), active: isGuidanceNavActive(GUIDANCE_DEPTHS.COMPANY, GUIDANCE_VIEWS.REVISIONS) }),
          ],
        },
      ],
    },
    SHOW_FILING_CHANGES && {
      id: 'filing-changes',
      label: 'Filing Analytics',
      subtitle: 'Disclosure change signals',
      description: 'Analyze flagged disclosure changes from company filings by vendor and company.',
      icon: <FindInPageIcon />,
      badge: 'Preview',
      chips: ['Filings', 'Disclosures', 'Changes'],
      compactShortcuts: true,
      defaultAction: navigateToFilingChangesNav(FILING_CHANGES_DEPTHS.VENDOR),
      active: location.pathname.startsWith('/filingchanges'),
      shortcutGroups: [
        {
          label: 'Vendor',
          icon: <InsightsIcon />,
          shortcuts: [
            buildShortcut({
              label: 'Events',
              description: 'Screen filing changes across covered companies',
              icon: <GridViewIcon />,
              onClick: navigateToFilingChangesNav(FILING_CHANGES_DEPTHS.VENDOR),
              active: isFilingChangesNavActive(FILING_CHANGES_DEPTHS.VENDOR),
            }),
          ],
        },
        {
          label: 'Company Detail',
          icon: <BusinessIcon />,
          shortcuts: [
            buildShortcut({
              label: 'Events',
              description: 'Review filing changes for a selected company',
              icon: <FindInPageIcon />,
              onClick: navigateToFilingChangesNav(FILING_CHANGES_DEPTHS.COMPANY),
              active: isFilingChangesNavActive(FILING_CHANGES_DEPTHS.COMPANY),
            }),
          ],
        },
      ],
    },
    SHOW_SI_HIRING && {
      id: 'partner-trends',
      label: 'Partner Trends',
      subtitle: 'SI ecosystem demand',
      description: 'Track system integrator hiring tied to vendor ecosystems and implementation capacity.',
      icon: <WorkIcon />,
      badge: 'Preview',
      chips: ['Alt Data', 'Partners', 'SI'],
      compactShortcuts: true,
      defaultAction: navigateToSIHiringNav(SI_HIRING_SECTIONS.VENDOR),
      active: location.pathname.startsWith('/sihiring'),
      shortcutGroups: [
        {
          label: 'Market',
          icon: <PublicIcon />,
          shortcuts: [
            buildShortcut({ label: 'Dashboard', description: 'Segmented and total partner demand', icon: <TimelineIcon />, onClick: navigateToSIHiringNav(SI_HIRING_SECTIONS.CATEGORY), active: isSIHiringNavActive(SI_HIRING_SECTIONS.CATEGORY) }),
          ],
        },
        {
          label: 'Vendor',
          icon: <InsightsIcon />,
          shortcuts: [
            buildShortcut({ label: 'Dashboard', description: 'Vendor hiring dashboard', icon: <TimelineIcon />, onClick: navigateToSIHiringNav(SI_HIRING_SECTIONS.VENDOR), active: isSIHiringNavActive(SI_HIRING_SECTIONS.VENDOR) }),
          ],
        },
        {
          label: 'Company Detail',
          icon: <BusinessIcon />,
          shortcuts: [
            buildShortcut({ label: 'Dashboard', description: 'Partner hiring trend', icon: <ShowChartIcon />, onClick: navigateToSIHiringNav(SI_HIRING_SECTIONS.COMPANY, SI_HIRING_VIEWS.DASHBOARD), active: isSIHiringNavActive(SI_HIRING_SECTIONS.COMPANY, SI_HIRING_VIEWS.DASHBOARD) }),
          ],
        },
      ],
    },
  ].filter(Boolean);

  // Retained for preview navigation while Coverage now points to the public directory.
  // eslint-disable-next-line no-unused-vars
  const marketMenuItems = [
    {
      id: 'coverage',
      label: 'Coverage',
      subtitle: 'Company universe',
      description: 'Explore tracked software companies, categories, and ownership status.',
      icon: <PublicIcon />,
      chips: ['Companies', 'Categories'],
      defaultAction: navigateToMarketCoverageNav(MARKET_COVERAGE_SECTIONS.COVERAGE, MARKET_COVERAGE_VIEWS.COVERAGE_TABLE),
      active: location.pathname.startsWith('/marketinsights/coverage/coverage') || location.pathname === '/marketinsights/coverage',
      shortcutGroups: [
        {
          label: 'Views',
          shortcuts: [
            buildShortcut({ label: 'Coverage Table', icon: <PublicIcon />, onClick: navigateToMarketCoverageNav(MARKET_COVERAGE_SECTIONS.COVERAGE, MARKET_COVERAGE_VIEWS.COVERAGE_TABLE), active: isMarketCoverageNavActive(MARKET_COVERAGE_SECTIONS.COVERAGE, MARKET_COVERAGE_VIEWS.COVERAGE_TABLE) }),
          ],
        },
      ],
    },
    {
      id: 'acquisitions',
      label: 'Acquisitions',
      subtitle: 'M&A comps and trends',
      description: 'Review market-level acquisition activity, category trends, and transaction lists.',
      icon: <BusinessIcon />,
      chips: ['M&A', 'Categories', 'Transactions'],
      defaultAction: navigateToMarketCoverageNav(MARKET_COVERAGE_SECTIONS.ACQUISITIONS, MARKET_COVERAGE_VIEWS.PUBLIC_ACQUISITIONS),
      active: location.pathname.startsWith('/marketinsights/coverage/acquisitions') || location.pathname.startsWith('/marketinsights/acquisitions'),
      shortcutGroups: [
        {
          label: 'Views',
          shortcuts: [
            ...(SHOW_FULL_ACQUISITIONS ? [
              buildShortcut({ label: 'Market', icon: <BarChartIcon />, onClick: navigateToMarketCoverageNav(MARKET_COVERAGE_SECTIONS.ACQUISITIONS, MARKET_COVERAGE_VIEWS.ACQUISITION_MARKET), active: isMarketCoverageNavActive(MARKET_COVERAGE_SECTIONS.ACQUISITIONS, MARKET_COVERAGE_VIEWS.ACQUISITION_MARKET) }),
              buildShortcut({ label: 'Category', icon: <CategoryIcon />, onClick: navigateToMarketCoverageNav(MARKET_COVERAGE_SECTIONS.ACQUISITIONS, MARKET_COVERAGE_VIEWS.ACQUISITION_CATEGORY), active: isMarketCoverageNavActive(MARKET_COVERAGE_SECTIONS.ACQUISITIONS, MARKET_COVERAGE_VIEWS.ACQUISITION_CATEGORY) }),
              buildShortcut({ label: 'List', icon: <GridOnIcon />, onClick: navigateToMarketCoverageNav(MARKET_COVERAGE_SECTIONS.ACQUISITIONS, MARKET_COVERAGE_VIEWS.ACQUISITION_LIST), active: isMarketCoverageNavActive(MARKET_COVERAGE_SECTIONS.ACQUISITIONS, MARKET_COVERAGE_VIEWS.ACQUISITION_LIST) }),
            ] : []),
            buildShortcut({ label: 'Public Deals', icon: <BusinessIcon />, onClick: navigateToMarketCoverageNav(MARKET_COVERAGE_SECTIONS.ACQUISITIONS, MARKET_COVERAGE_VIEWS.PUBLIC_ACQUISITIONS), active: isMarketCoverageNavActive(MARKET_COVERAGE_SECTIONS.ACQUISITIONS, MARKET_COVERAGE_VIEWS.PUBLIC_ACQUISITIONS) }),
          ],
        },
      ],
    },
    {
      id: 'venture-private',
      label: 'Venture',
      subtitle: 'Funding data',
      description: 'Track venture-backed software funding and valuation coverage.',
      icon: <RocketLaunchIcon />,
      chips: ['Funding', 'Valuation'],
      defaultAction: navigateToMarketCoverageNav(MARKET_COVERAGE_SECTIONS.PRIVATE_FUNDING, MARKET_COVERAGE_VIEWS.VENTURE_PRIVATE),
      active: location.pathname.startsWith('/marketinsights/coverage/private-funding') || location.pathname.startsWith('/marketinsights/venture-backed'),
      shortcutGroups: [
        {
          label: 'Views',
          shortcuts: [
            buildShortcut({ label: 'Venture', icon: <RocketLaunchIcon />, onClick: navigateToMarketCoverageNav(MARKET_COVERAGE_SECTIONS.PRIVATE_FUNDING, MARKET_COVERAGE_VIEWS.VENTURE_PRIVATE), active: isMarketCoverageNavActive(MARKET_COVERAGE_SECTIONS.PRIVATE_FUNDING, MARKET_COVERAGE_VIEWS.VENTURE_PRIVATE) }),
          ],
        },
      ],
    },
    SHOW_EXCHANGE_VOLUME && {
      id: 'exchange-volume',
      label: 'Exchange Volume',
      subtitle: 'Options and futures data',
      description: 'Analyze exchange volume trends, data tables, and market share.',
      icon: <CurrencyExchangeIcon />,
      badge: 'Preview',
      chips: ['Markets', 'Volume', 'Share'],
      defaultAction: navigateToExchangeVolumeView('trends'),
      active: location.pathname.startsWith('/marketinsights/exchangevolume'),
      shortcutGroups: [
        {
          label: 'Views',
          shortcuts: [
            buildShortcut({ label: 'Dashboard', icon: <TimelineIcon />, onClick: navigateToExchangeVolumeView('trends'), active: isExchangeVolumeViewActive('trends') }),
            buildShortcut({ label: 'Data Table', icon: <GridOnIcon />, onClick: navigateToExchangeVolumeView('table'), active: isExchangeVolumeViewActive('table') }),
            buildShortcut({ label: 'Market Share', icon: <DonutSmallIcon />, onClick: navigateToExchangeVolumeView('marketShare'), active: isExchangeVolumeViewActive('marketShare') }),
          ],
        },
      ],
    },
  ].filter(Boolean);

  const getActiveMenuItem = (items) => (
    items.find((item) => item.id === activeMenuItemId)
    || items.find((item) => item.active)
    || items[0]
  );

  const MenuShortcutButton = ({ shortcut, compact = false }) => {
    const showDescription = Boolean(shortcut.description) && !compact;
    return (
      <Button
        key={shortcut.label}
        startIcon={shortcut.icon ? renderIcon(shortcut.icon, 16) : null}
        onClick={shortcut.onClick}
        sx={menuShortcutButtonSx(theme, shortcut.active, showDescription)}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ fontSize: '12px', lineHeight: 1.15, fontWeight: shortcut.active ? 800 : 700, color: 'inherit' }}>
            {shortcut.label}
          </Box>
          {showDescription && (
            <Box
              sx={{
                mt: 0.25,
                fontSize: '10.5px',
                lineHeight: 1.2,
                color: shortcut.active ? 'inherit' : theme.palette.text.secondary,
                whiteSpace: 'normal',
              }}
            >
              {shortcut.description}
            </Box>
          )}
        </Box>
      </Button>
    );
  };

  const MenuShortcutGroup = ({ group, compact = false }) => (
    <Box sx={{ mb: 1.25 }}>
      <Box sx={{ ...menuSectionTitleSx(theme), mb: 0.75 }}>{group.label}</Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {group.shortcuts.map((shortcut) => (
          <MenuShortcutButton key={shortcut.label} shortcut={shortcut} compact={compact} />
        ))}
      </Box>
    </Box>
  );

  const MenuDepthShortcutGroups = ({ groups }) => (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ ...menuSectionTitleSx(theme), mb: 0.75 }}>Depth</Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {groups.map((group) => {
          const active = group.shortcuts.some((shortcut) => shortcut.active);
          const defaultShortcut = group.shortcuts[0];
          const DepthIcon = group.icon || defaultShortcut?.icon;
          return (
            <Box
              key={group.label}
              tabIndex={0}
              sx={{
                p: 0.75,
                borderRadius: 1,
                border: `1px solid ${active ? theme.palette.primary.main : theme.palette.divider}`,
                backgroundColor: active ? getHighlightColor(theme, 0.5) : 'transparent',
                transition: 'background-color 140ms ease, border-color 140ms ease',
                outline: 'none',
                '&:hover, &:focus-within': {
                  backgroundColor: getHighlightColor(theme, 0.45),
                  borderColor: theme.palette.primary.main,
                },
                '& .depth-views': {
                  maxHeight: active ? 132 : 0,
                  opacity: active ? 1 : 0,
                  mt: active ? 0.75 : 0,
                  overflow: 'hidden',
                  transition: 'max-height 160ms ease, opacity 120ms ease, margin-top 160ms ease',
                },
                '&:hover .depth-views, &:focus-within .depth-views': {
                  maxHeight: 148,
                  opacity: 1,
                  mt: 0.75,
                },
              }}
            >
              <Button
                fullWidth
                startIcon={DepthIcon ? renderIcon(DepthIcon, 16) : null}
                onClick={defaultShortcut?.onClick}
                sx={{
                  justifyContent: 'flex-start',
                  minHeight: 28,
                  px: 0.5,
                  py: 0.25,
                  borderRadius: 0.75,
                  textTransform: 'none',
                  color: active ? theme.palette.primary.main : theme.palette.text.primary,
                  '& .MuiButton-startIcon': { mr: 0.75, ml: 0 },
                  '&:hover': {
                    backgroundColor: getHighlightColor(theme, 0.4),
                    color: active ? theme.palette.primary.main : theme.palette.text.primary,
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, width: '100%' }}>
                  <Box sx={{ fontSize: '12px', fontWeight: 800 }}>
                    {group.label}
                  </Box>
                </Box>
              </Button>
              <Box className="depth-views">
                <Box sx={{ mb: 0.55, pl: 0.5, fontSize: '10px', fontWeight: 800, color: theme.palette.text.secondary, textTransform: 'uppercase' }}>
                  Views in {group.label}
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.65 }}>
                {group.shortcuts.map((shortcut) => (
                  <MenuShortcutButton key={shortcut.label} shortcut={shortcut} compact />
                ))}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  const MenuPreviewDetail = ({ item }) => (
    <Box sx={menuDetailPanelSx(theme)}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 42,
            height: 42,
            borderRadius: 1,
            backgroundColor: theme.palette.primary.main,
            color: '#fff',
            flex: '0 0 auto',
          }}
        >
          {renderIcon(item.icon, 21)}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <Box sx={{ fontWeight: 800, fontSize: '16px', color: theme.palette.text.primary }}>
              {item.label}
            </Box>
            {item.badge && (
              <Chip
                label={item.badge}
                size="small"
                sx={{
                  height: 16,
                  fontSize: '8px',
                  fontWeight: 700,
                  backgroundColor: theme.palette.warning.main,
                  color: theme.palette.warning.contrastText,
                  '& .MuiChip-label': { px: 0.6 },
                }}
              />
            )}
          </Box>
          <Box sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>
            {item.subtitle}
          </Box>
        </Box>
      </Box>

      <Box sx={{ fontSize: '13px', lineHeight: 1.45, color: theme.palette.text.secondary, mb: 1.25 }}>
        {item.description}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
        {item.chips.map((chip) => (
          <Chip
            key={chip}
            label={chip}
            size="small"
            sx={{ height: 20, fontSize: '10px', fontWeight: 700 }}
          />
        ))}
      </Box>

      <Button
        onClick={item.defaultAction}
        sx={{
          alignSelf: 'flex-start',
          textTransform: 'none',
          fontSize: '12px',
          fontWeight: 700,
          mb: 1.5,
          px: 1.25,
          py: 0.55,
          borderRadius: 1,
          color: theme.palette.primary.contrastText,
          backgroundColor: theme.palette.primary.main,
          '&:hover': { backgroundColor: theme.palette.primary.dark },
        }}
      >
        Open {item.label}
      </Button>

      <Box sx={{ overflowY: 'auto', pr: 0.25 }}>
        {item.compactShortcuts ? (
          <MenuDepthShortcutGroups groups={item.shortcutGroups} />
        ) : (
          item.shortcutGroups.map((group) => (
            <MenuShortcutGroup key={group.label} group={group} />
          ))
        )}
      </Box>
    </Box>
  );

  const MenuListItem = ({ item, active }) => (
    <Box
      component="button"
      type="button"
      onMouseEnter={() => setActiveMenuItemId(item.id)}
      onFocus={() => setActiveMenuItemId(item.id)}
      onClick={item.defaultAction}
      sx={menuListButtonSx(theme, active)}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 34,
          height: 34,
          borderRadius: 1,
          backgroundColor: active ? theme.palette.primary.main : getHighlightColor(theme, 0.45),
          color: active ? '#fff' : theme.palette.primary.main,
        }}
      >
        {renderIcon(item.icon, 18)}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ fontWeight: 750, fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.label}
          </Box>
          {item.badge && (
            <Chip
              label={item.badge}
              size="small"
              sx={{
                height: 14,
                fontSize: '8px',
                fontWeight: 700,
                '& .MuiChip-label': { px: 0.5 },
              }}
            />
          )}
        </Box>
        <Box sx={{ fontSize: '11px', color: theme.palette.text.secondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.subtitle}
        </Box>
      </Box>
    </Box>
  );

  // eslint-disable-next-line no-unused-vars
  const DesktopMenuContent = ({ title, items }) => {
    const activeItem = getActiveMenuItem(items);
    return (
      <Box sx={menuPreviewLayoutSx(theme)}>
        <Box sx={menuListPanelSx(theme)}>
          <Box sx={menuSectionTitleSx(theme)}>{title}</Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}>
            {items.map((item) => (
              <MenuListItem
                key={item.id}
                item={item}
                active={item.id === activeItem.id}
              />
            ))}
          </Box>
        </Box>
        <MenuPreviewDetail item={activeItem} />
      </Box>
    );
  };

  // eslint-disable-next-line no-unused-vars
  const MobileMenuContent = ({ items }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {items.map((item) => (
        <Accordion key={item.id} elevation={0} sx={accordionSx(theme)}>
          <AccordionSummary sx={{ ...accordionSummarySx(theme), px: 0 }} expandIcon={<ArrowDropDownIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  backgroundColor: getHighlightColor(theme, 0.45),
                  color: theme.palette.primary.main,
                  flex: '0 0 auto',
                }}
              >
                {renderIcon(item.icon, 17)}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                    {item.label}
                  </Box>
                  {item.badge && <Chip label={item.badge} size="small" sx={{ height: 15, fontSize: '8px' }} />}
                </Box>
                <Box sx={{ fontSize: '11px', color: theme.palette.text.secondary }}>
                  {item.subtitle}
                </Box>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, px: 0.5 }}>
            <Box sx={{ fontSize: '12px', lineHeight: 1.45, color: theme.palette.text.secondary, mb: 1 }}>
              {item.description}
            </Box>
            <Button
              onClick={item.defaultAction}
              sx={{
                ...mobileMenuPrimaryButtonSx(theme),
                mb: 1,
                width: '100%',
              }}
            >
              Open {item.label}
            </Button>
            {item.shortcutGroups.map((group) => (
              <MenuShortcutGroup key={group.label} group={group} compact={item.compactShortcuts} />
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <ThemeProvider theme={theme}>
      <Box
        aria-hidden="true"
        data-testid="header-background"
        sx={getHeaderBackdropSx(theme)}
      />
      <AppBar
      elevation={0}
      position="fixed"
      sx={getHeaderFrameSx(theme)}
    >
      {/* ================================================================== */}
      {/* Single Unified Navigation Bar */}
      {/* ================================================================== */}
      <Box
        sx={getHeaderShellSx(theme)}
      >
        {/* Left: Logo */}
        {!isDesktop && (
          <a href={marketingBaseUrl} aria-label="Go to SoftwareIQ home page">
            <img
              src={
                themeMode === 'light'
                  ? require('../../data/images/logo.png')
                  : require('../../data/images/logodark.png')
              }
              alt="SoftwareIQ Logo"
              className="logo"
              style={{ height: '20px', width: 'auto' }}
            />
          </a>
        )}

        {/* Mobile Logo */}
        {isDesktop && (
          <a href={marketingBaseUrl} aria-label="Go to SoftwareIQ home page">
            <img
              src={
                themeMode === 'light'
                  ? require('../../data/images/logo.png')
                  : require('../../data/images/logodark.png')
              }
              alt="SoftwareIQ Logo"
              className="logo"
              style={{ height: '20px', width: 'auto' }}
            />
          </a>
        )}

        {/* Desktop Navigation */}
        {!isDesktop && (
          <>
            {/* Primary navigation controls */}
            <Box
              ref={dropdownAnchorRef}
              sx={{ display: 'flex', ml: 2.5, gap: 'var(--siq-header-nav-control-gap)' }}
            >
              {/* Datasets Button */}
              <Button
                onClick={handleNavButtonClick('metrics')}
                aria-haspopup="true"
                aria-expanded={dropdownOpen && activeSection === 'metrics'}
                endIcon={
                  <ArrowDropDownIcon
                    sx={{
                      fontSize: '18px',
                      transition: 'transform 0.2s ease',
                      transform: dropdownOpen && activeSection === 'metrics' ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  />
                }
                sx={{
                  ...getNavButtonSx(
                    theme,
                    isActiveSection('metrics'),
                    true,
                    dropdownOpen && activeSection === 'metrics'
                  ),
                  position: 'relative',
                }}
              >
                Datasets
              </Button>


              <Button
                component={RouterLink}
                to="/documentation"
                aria-current={location.pathname.startsWith('/documentation') || location.pathname === '/coverage' ? 'page' : undefined}
                onClick={closeAllMenus}
                sx={{
                  ...getNavButtonSx(theme, location.pathname.startsWith('/documentation') || location.pathname === '/coverage'),
                  position: 'relative',
                }}
              >
                Documentation
              </Button>
              {getDevFlag('MCP') && <Button component={RouterLink} to="/mcp" onClick={closeAllMenus}
                aria-current={location.pathname === '/mcp' ? 'page' : undefined}
                sx={getNavButtonSx(theme, location.pathname === '/mcp')}>MCP</Button>}
            </Box>

            {/* Unified Dropdown Panel */}
            <Popper
              open={dropdownOpen}
              anchorEl={dropdownAnchorRef.current}
              placement="bottom-start"
              transition
              sx={{ zIndex: 1300 }}
            >
              {({ TransitionProps }) => (
                <ClickAwayListener onClickAway={closeAllMenus}>
                  <Box>
                    <Grow {...TransitionProps} timeout={200}>
                      <Paper sx={dropdownPaperSx(theme)}>
                        <Box
                          sx={{
                            width: 'auto',
                            overflow: 'hidden',
                          }}
                        >
                          <ResearchDatasetMenu onNavigate={closeAllMenus} />
                        </Box>
                      </Paper>
                    </Grow>
                  </Box>
                </ClickAwayListener>
              )}
            </Popper>

            {/* Spacer */}
            <Box sx={{ flex: 1 }} />

            {/* Right: Search, Auth */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Security Analysis Search */}
              <Box sx={searchWrapperSx}>
                <Box sx={getHeaderSearchContainerSx(theme, inlineSearchFocused)}>
                  <Autocomplete
                    freeSolo
                    size="small"
                    disableClearable
                    forcePopupIcon={false}
                    open={inlineSearchOpen}
                    onOpen={() => setInlineSearchOpen(true)}
                    onClose={() => setInlineSearchOpen(false)}
                    options={filteredCompanyOptions}
                    getOptionLabel={(o) =>
                      typeof o === 'string'
                        ? o
                        : o.entityKind === 'product'
                          ? o.name
                          : o.isNoResults
                          ? o.companyName
                          : formatCompanyDisplayLabel({ ticker: o.ticker, companyName: o.companyName })
                    }
                    inputValue={companyQuery}
                    onInputChange={(_, v, reason) => {
                      if (reason === 'reset') return;
                      setCompanyQuery(v);
                    }}
                    filterOptions={(options) => {
                      return buildCompanySearchOptions(options, recentCompanyOptions, companyQuery, productIndex);
                    }}
                    groupBy={(option) => option.searchGroup}
                    renderGroup={renderCompanySearchGroup}
                    getOptionDisabled={(option) => Boolean(option.isNoResults)}
                    onChange={(_, v) => {
                      if (!v || v.isNoResults) return;
                      if (v.entityKind === 'product') {
                        navigate(productPath(v.productKey));
                        setCompanyQuery('');
                        setInlineSearchOpen(false);
                        setInlineSearchFocused(false);
                        setTimeout(() => searchInputRef.current?.blur(), 0);
                        return;
                      }
                      const selected = typeof v === 'string'
                        ? filteredCompanyOptions.find(
                            (x) =>
                              (!isPrivateCompanyIdentifier(x.ticker)
                                && x.ticker.toLowerCase() === v.trim().toLowerCase()) ||
                              x.companyName.toLowerCase() === v.trim().toLowerCase()
                          )
                        : v;
                      if (!selected) return;
                      goToCompany(selected);
                      setCompanyQuery('');
                      setInlineSearchOpen(false);
                      setInlineSearchFocused(false);
                      setTimeout(() => searchInputRef.current?.blur(), 0);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Find a company"
                        onFocus={() => {
                          setInlineSearchFocused(true);
                          setCompanyQuery('');
                          setInlineSearchOpen(true);
                        }}
                        onBlur={() => {
                          setInlineSearchFocused(false);
                          setCompanyQuery('');
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const typed = companyQuery.trim();
                            if (typed) {
                              const match = filteredCompanyOptions.find(
                                (x) =>
                                  (!isPrivateCompanyIdentifier(x.ticker)
                                    && x.ticker.toLowerCase() === typed.toLowerCase()) ||
                                  x.companyName.toLowerCase() === typed.toLowerCase()
                              );
                              if (match) {
                                goToCompany(match);
                                setCompanyQuery('');
                                setInlineSearchOpen(false);
                                setInlineSearchFocused(false);
                                setTimeout(() => searchInputRef.current?.blur(), 0);
                              }
                            }
                          }
                        }}
                        inputRef={searchInputRef}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon
                                sx={{
                                  fontSize: '17px',
                                  color: inlineSearchFocused
                                    ? theme.palette.primary.main
                                    : theme.palette.text.secondary,
                                  transition: 'color 0.2s ease',
                                }}
                              />
                            </InputAdornment>
                          ),
                          sx: getHeaderSearchInputSx(theme),
                        }}
                      />
                    )}
                    slotProps={{
                      popper: { sx: { width: 240 } },
                      listbox: { sx: { maxHeight: 320, p: 0 } },
                    }}
                    PaperComponent={(props) => (
                      <CompanySearchDropdownPaper
                        {...props}
                        selectedTypes={selectedCompanyTypes}
                        onChange={setSelectedCompanyTypes}
                        sx={{
                          width: 240,
                          mt: 0.5,
                          backgroundColor: theme.background?.transparent || 'rgba(255,255,255,0.95)',
                          backdropFilter: 'blur(8px)',
                          borderRadius: '8px',
                          boxShadow: theme.palette.mode === 'light'
                            ? '0 4px 20px rgba(0,0,0,0.12)'
                            : '0 4px 20px rgba(0,0,0,0.4)',
                        }}
                      />
                    )}
                    renderOption={(props, option) => {
                      if (option.isNoResults) {
                        const { key, ...optionProps } = props;
                        return (
                          <li key={key} {...optionProps}>
                            <Box sx={{ px: 1.5, py: 0.65, fontSize: '12px', color: 'text.secondary' }}>
                              No results found
                            </Box>
                          </li>
                        );
                      }

                      const { key, ...optionProps } = props;
                      if (option.entityKind === 'product') {
                        return (
                          <li key={key} {...optionProps}>
                            <Box sx={{ px: 1.5, py: 0.6, minWidth: 0 }}>
                              <Box sx={{ fontSize: '12.5px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {option.name}
                              </Box>
                              <Box sx={{ fontSize: '11px', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {[productOwnerLabel(option), option.offeringType === 'family' ? 'Family' : null].filter(Boolean).join(' · ')}
                              </Box>
                            </Box>
                          </li>
                        );
                      }
                      return (
                        <CompanySearchOptionRow
                          key={key}
                          option={option}
                          theme={theme}
                          expandable
                          onNavigate={(nextTicker, destination, selectedOption) => {
                            updateRecentSearches(selectedOption.companyName);
                            navigate(`/company/${nextTicker.toLowerCase()}/${destination}`);
                            setCompanyQuery('');
                            setInlineSearchOpen(false);
                            setInlineSearchFocused(false);
                            setTimeout(() => searchInputRef.current?.blur(), 0);
                          }}
                          {...optionProps}
                        />
                      );
                    }}
                  />
                </Box>
              </Box>

              {/* Theme Toggle */}
              <IconButton
                sx={getHeaderIconActionSx(theme)}
                onClick={toggleTheme}
                aria-label={themeMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                aria-pressed={themeMode === 'dark'}
              >
                {themeMode === 'dark' ? <DarkModeIcon sx={{ fontSize: 20 }} /> : <LightModeIcon sx={{ fontSize: 20 }} />}
              </IconButton>

              {/* Auth */}
              {!isLoading && !isAuthenticated && <LoginButton />}
              <UserMenu toggleTheme={toggleTheme} />
            </Box>
          </>
        )}

        {/* ================================================================ */}
        {/* Mobile Navigation */}
        {/* ================================================================ */}
        {isDesktop && (
          <>
            {/* Spacer to push icons to right */}
            <Box sx={{ flex: 1 }} />

            {/* Mobile Theme Toggle */}
            <IconButton
              sx={getHeaderIconActionSx(theme)}
              onClick={toggleTheme}
              aria-label={themeMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-pressed={themeMode === 'dark'}
            >
              {themeMode === 'dark' ? <DarkModeIcon sx={{ fontSize: 20 }} /> : <LightModeIcon sx={{ fontSize: 20 }} />}
            </IconButton>

            {/* Mobile Search Button */}
            <Button
              sx={{ ...getHeaderIconActionSx(theme), ml: 0.5 }}
              aria-controls="search-drawer"
              aria-haspopup="true"
              onClick={() => setSearchOpen(true)}
              aria-label="Open search"
            >
              <SearchIcon sx={{ fontSize: '24px' }} />
            </Button>

            <Drawer
              id="search-drawer"
              anchor="right"
              open={searchOpen}
              onClose={() => setSearchOpen(false)}
              elevation={0}
              sx={{
                '& .MuiDrawer-paper': {
                  backgroundColor: theme.chrome?.menuSurface || theme.palette.background.paper,
                  width: '100vw',
                  height: 'calc(100vh - 48px)',
                  top: '48px',
                },
              }}
            >
              <Box sx={{ p: 2.5 }}>
                <TextField
                  variant="outlined"
                  size="small"
                  autoComplete="off"
                  fullWidth
                  value={mobileQuery}
                  onChange={handleMobileSearchChange}
                  placeholder="Find a company"
                  sx={getMobileHeaderSearchSx(theme)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ color: theme.palette.text.secondary, fontSize: 18, mr: 0.75 }} />,
                  }}
                />
              </Box>

              <Box
                sx={{
                  backgroundColor: 'transparent',
                  backdropFilter: 'blur(3px)',
                  borderBottomLeftRadius: '6px',
                  borderBottomRightRadius: '6px',
                }}
              >
                <Box
                  sx={{
                    p: '8px 12px',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.035)'
                      : 'rgba(0, 0, 0, 0.025)',
                  }}
                >
                  <CompanyTypeFilter
                    selectedTypes={selectedCompanyTypes}
                    onChange={setSelectedCompanyTypes}
                    size="medium"
                  />
                </Box>

                {mobileQuery.trim().length >= MIN_COMPANY_SEARCH_CHARS && (
                  <>
                    <Box
                      sx={{
                        p: '4px 10px',
                        fontWeight: 'bold',
                        fontSize: '12px',
                        color: theme.palette.text.secondary,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      Search Results
                    </Box>
                    {generateLinks(filteredItems)}
                    {filteredItems.length === 0 && (
                      <Box sx={{ px: 1.25, py: 0.75, color: theme.palette.text.secondary, fontSize: '12px' }}>No results found.</Box>
                    )}
                  </>
                )}

                {mobileRecentItems.length > 0 && (
                  <>
                    <Box
                      sx={{
                        p: '4px 10px',
                        fontWeight: 'bold',
                        fontSize: '12px',
                        color: theme.palette.text.secondary,
                        borderTop: filteredItems.length > 0 ? `1px solid ${theme.palette.divider}` : 'none',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      Recent Searches
                    </Box>
                    {generateLinks(mobileRecentItems)}
                  </>
                )}
              </Box>
            </Drawer>

            {/* Mobile Menu Button */}
            <Button
              sx={{ p: 0, textTransform: 'none', minWidth: 'auto', ml: 1 }}
              aria-controls="mobile-nav-drawer"
              aria-haspopup="true"
              onClick={() => setMobileNavOpen(!mobileNavOpen)}
              aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileNavOpen ? (
                <CloseIcon sx={{ fontSize: '24px' }} />
              ) : (
                <MenuIcon sx={{ fontSize: '24px' }} />
              )}
            </Button>

            <Drawer
              id="mobile-nav-drawer"
              anchor="right"
              open={mobileNavOpen}
              onClose={closeAllMenus}
              elevation={0}
              sx={{
                '& .MuiDrawer-paper': {
                  backgroundColor: theme.chrome?.menuSurface || theme.palette.background.paper,
                  width: '100vw',
                  height: 'calc(100vh - 48px)',
                  top: '48px',
                  overflowY: 'auto',
                },
              }}
            >
              {/* Auth section at top of mobile menu */}
              <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                {!isLoading && !isAuthenticated && <LoginButton mobile />}
                <UserMenu toggleTheme={toggleTheme} />
              </Box>

              <Box sx={{ px: 1.5, pt: 2 }}><Typography fontWeight={600}>Datasets</Typography></Box>
              <ResearchDatasetMenu mobile onNavigate={closeAllMenus} />

              <Button
                component={RouterLink}
                to="/documentation"
                aria-current={location.pathname.startsWith('/documentation') || location.pathname === '/coverage' ? 'page' : undefined}
                onClick={closeAllMenus}
                sx={mobileNavigationLinkSx(theme)}
              >
                Documentation
              </Button>
              {getDevFlag('MCP') && <Button component={RouterLink} to="/mcp" onClick={closeAllMenus}
                aria-current={location.pathname === '/mcp' ? 'page' : undefined}
                sx={mobileNavigationLinkSx(theme)}>MCP</Button>}

              {false && (
                <>
              {/* Datasets Accordion */}
              <Accordion
                elevation={0}
                sx={accordionSx(theme)}
                expanded={accordionOpen.industryMetrics}
                onChange={toggleAccordion('industryMetrics')}
              >
                <AccordionSummary
                  sx={accordionSummarySx(theme)}
                  expandIcon={<ArrowDropDownIcon />}
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', fontSize: '16px' }}
                  >
                    Datasets
                  </Box>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    color: theme.palette.text.secondary,
                    backgroundColor: 'transparent',
                    pt: 0,
                  }}
                >
                  {/* Operating KPIs Section Label */}
                  <Box sx={menuSectionTitleSx(theme)}>Operating KPIs</Box>

                  {/* Operating KPIs Hero Item */}
                  <Box
                    onClick={navigateToMetricsView('table')}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      p: 1.25,
                      mt: 0.25,
                      mb: 0.25,
                      borderRadius: 1.25,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: theme.palette.mode === 'light'
                        ? 'rgba(63, 100, 168, 0.08)'
                        : 'rgba(145, 166, 205, 0.12)',
                      border: `1px solid ${theme.palette.mode === 'light'
                        ? 'rgba(63, 100, 168, 0.15)'
                        : 'rgba(145, 166, 205, 0.2)'}`,
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'light'
                          ? 'rgba(63, 100, 168, 0.15)'
                          : 'rgba(145, 166, 205, 0.2)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        backgroundColor: theme.palette.primary.main,
                        color: '#fff',
                      }}
                    >
                      <ShowChartIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Box sx={{ fontWeight: 600, fontSize: '14px', color: theme.palette.text.primary }}>
                        Operating KPIs
                      </Box>
                      <Box sx={{ fontSize: '11px', color: theme.palette.text.secondary }}>
                        Public software company data
                      </Box>
                    </Box>
                  </Box>

                  {/* Operating KPIs Sub-items */}
                  <Box sx={{ pl: 2, mt: 1 }}>
                    <MenuLink
                      to={getOperatingMetricsNavPath(OPERATING_METRICS_DEPTHS.VENDOR, 'table')}
                      icon={<GridViewIcon />}
                      label="Comp Sheet"
                      description="Side-by-side company data"
                      onClick={navigateToMetricsView('table')}
                      isActive={isMetricsViewActive('table')}
                    />
                    <MenuLink
                      to={getOperatingMetricsNavPath(OPERATING_METRICS_DEPTHS.VENDOR, 'chart')}
                      icon={<BarChartIcon />}
                      label="Current Metrics"
                      description="KPI, valuation, acceleration, and efficiency"
                      onClick={navigateToMetricsView('chart')}
                      isActive={['chart', 'scatter', 'acceleration', 'quadrant'].some((view) => isMetricsViewActive(view))}
                    />
                    <MenuLink
                      to={getOperatingMetricsNavPath(OPERATING_METRICS_DEPTHS.CATEGORY, OPERATING_CATEGORY_VIEWS.DISTRIBUTION)}
                      icon={<CandlestickChartIcon />}
                      label="Metric Distribution"
                      description="Statistical range analysis"
                      onClick={navigateToMetricsView('trended')}
                      isActive={isMetricsViewActive('trended')}
                    />
                  </Box>

                  {/* Alternative Data Section Label */}
                  <Box sx={{ ...menuSectionTitleSx(theme), mt: 2.5 }}>Alternative Data</Box>

                  {/* Hiring Dashboard Hero Item */}
                  <Box
                    onClick={navigateToHiringNav(HIRING_DEPTHS.VENDOR, HIRING_VIEWS.SUMMARY)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      p: 1.25,
                      mt: 0.25,
                      mb: 0.25,
                      borderRadius: 1.25,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: theme.palette.mode === 'light'
                        ? 'rgba(63, 100, 168, 0.08)'
                        : 'rgba(145, 166, 205, 0.12)',
                      border: `1px solid ${theme.palette.mode === 'light'
                        ? 'rgba(63, 100, 168, 0.15)'
                        : 'rgba(145, 166, 205, 0.2)'}`,
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'light'
                          ? 'rgba(63, 100, 168, 0.15)'
                          : 'rgba(145, 166, 205, 0.2)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        backgroundColor: theme.palette.primary.main,
                        color: '#fff',
                      }}
                    >
                      <WorkHistoryIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Box sx={{ fontWeight: 600, fontSize: '14px', color: theme.palette.text.primary }}>
                        Hiring Dashboard
                      </Box>
                      <Box sx={{ fontSize: '11px', color: theme.palette.text.secondary }}>
                        Hiring trends & headcount
                      </Box>
                    </Box>
                  </Box>

                  {/* Hiring Dashboard Sub-items */}
                  <Box sx={{ pl: 2, mt: 1 }}>
                    <MenuLink
                      to={getHiringNavPath(HIRING_DEPTHS.COMPANY, HIRING_VIEWS.SUMMARY)}
                      icon={<BusinessIcon />}
                      label="Company Detail"
                      description="Single-company hiring detail"
                      onClick={navigateToHiringNav(HIRING_DEPTHS.COMPANY, HIRING_VIEWS.SUMMARY)}
                      isActive={isHiringNavActive(HIRING_DEPTHS.COMPANY, HIRING_VIEWS.SUMMARY)}
                    />
                    <MenuLink
                      to={getHiringNavPath(HIRING_DEPTHS.COMPANY, HIRING_VIEWS.JOB_BOARD)}
                      icon={<ViewListIcon />}
                      label="Company Job Board"
                      description="Active openings by company"
                      onClick={navigateToHiringNav(HIRING_DEPTHS.COMPANY, HIRING_VIEWS.JOB_BOARD)}
                      isActive={isHiringNavActive(HIRING_DEPTHS.COMPANY, HIRING_VIEWS.JOB_BOARD)}
                    />
                    <MenuLink
                      to={getHiringNavPath(HIRING_DEPTHS.VENDOR, HIRING_VIEWS.SUMMARY)}
                      icon={<InsightsIcon />}
                      label="Vendor Summary"
                      description="Vendor hiring summary"
                      onClick={navigateToHiringNav(HIRING_DEPTHS.VENDOR, HIRING_VIEWS.SUMMARY)}
                      isActive={isHiringNavActive(HIRING_DEPTHS.VENDOR, HIRING_VIEWS.SUMMARY)}
                    />
                    <MenuLink
                      to={getHiringNavPath(HIRING_DEPTHS.VENDOR, HIRING_VIEWS.ROLES)}
                      icon={<PieChartIcon />}
                      label="Vendor Roles"
                      description="Department distribution"
                      onClick={navigateToHiringNav(HIRING_DEPTHS.VENDOR, HIRING_VIEWS.ROLES)}
                      isActive={isHiringNavActive(HIRING_DEPTHS.VENDOR, HIRING_VIEWS.ROLES)}
                    />
                    <MenuLink
                      to={getHiringNavPath(HIRING_DEPTHS.VENDOR, HIRING_VIEWS.LEADERSHIP)}
                      icon={<WorkIcon />}
                      label="Vendor Leadership"
                      description="Leadership changes by vendor"
                      onClick={navigateToHiringNav(HIRING_DEPTHS.VENDOR, HIRING_VIEWS.LEADERSHIP)}
                      isActive={isHiringNavActive(HIRING_DEPTHS.VENDOR, HIRING_VIEWS.LEADERSHIP)}
                    />
                    <MenuLink
                      to={getHiringNavPath(HIRING_DEPTHS.CATEGORY, HIRING_VIEWS.SUMMARY)}
                      icon={<PublicIcon />}
                      label="Category Summary"
                      description="Category hiring signals"
                      onClick={navigateToHiringNav(HIRING_DEPTHS.CATEGORY, HIRING_VIEWS.SUMMARY)}
                      isActive={isHiringNavActive(HIRING_DEPTHS.CATEGORY, HIRING_VIEWS.SUMMARY)}
                    />
                  </Box>

                  {FEATURE_FLAGS.SHOW_DEVELOPER_SIGNALS && (
                    <>
                      {/* Divider between Hiring Dashboard and Adoption Signals */}
                      <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, mt: 2, mb: 1.5 }} />

                      {/* Adoption Signals Hero Item */}
                      <Box
                        onClick={navigateToDevView('overview')}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          p: 1.5,
                          mt: 0.5,
                          mb: 0.5,
                          borderRadius: 1.5,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          backgroundColor: theme.palette.mode === 'light'
                            ? 'rgba(63, 100, 168, 0.08)'
                            : 'rgba(145, 166, 205, 0.12)',
                          border: `1px solid ${theme.palette.mode === 'light'
                            ? 'rgba(63, 100, 168, 0.15)'
                            : 'rgba(145, 166, 205, 0.2)'}`,
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'light'
                              ? 'rgba(63, 100, 168, 0.15)'
                              : 'rgba(145, 166, 205, 0.2)',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 36,
                            height: 36,
                            borderRadius: 1,
                            backgroundColor: theme.palette.primary.main,
                            color: '#fff',
                          }}
                        >
                          <CodeIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Box>
                          <Box sx={{ fontWeight: 600, fontSize: '14px', color: theme.palette.text.primary, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            Adoption Signals
                            <Chip
                              label="Preview"
                              size="small"
                              sx={{
                                height: 14,
                                fontSize: '8px',
                                fontWeight: 600,
                                backgroundColor: theme.palette.warning.main,
                                color: theme.palette.warning.contrastText,
                                '& .MuiChip-label': { px: 0.5 },
                              }}
                            />
                          </Box>
                          <Box sx={{ fontSize: '11px', color: theme.palette.text.secondary }}>
                            Open source & package data
                          </Box>
                        </Box>
                      </Box>

                      {/* Adoption Signals Sub-items */}
                      <Box sx={{ pl: 2, mt: 1 }}>
                        <MenuLink
                          to={getDeveloperNavPath(DEVELOPER_WORKSPACE_LAYERS.PROJECT, DEVELOPER_WORKSPACE_VIEWS.TRENDS)}
                          icon={<GridViewIcon />}
                          label="All Projects"
                          description="Browse & compare project metrics"
                          onClick={navigateToDevView('overview')}
                          isActive={isDevViewActive('overview')}
                        />
                        <MenuLink
                          to={getDeveloperNavPath(DEVELOPER_WORKSPACE_LAYERS.PROJECT_DETAIL, DEVELOPER_WORKSPACE_VIEWS.PACKAGES)}
                          icon={<InsightsIcon />}
                          label="Project Analysis"
                          description="Deep dive with comparisons"
                          onClick={navigateToDevView('detail')}
                          isActive={isDevViewActive('detail')}
                        />
                        {SHOW_DEVELOPER_LEADERBOARD && (
                          <MenuLink
                            to={getDeveloperNavPath(DEVELOPER_WORKSPACE_LAYERS.PROJECT, DEVELOPER_WORKSPACE_VIEWS.LEADERBOARD)}
                            icon={<TrendingUpIcon />}
                            label="Leaderboard"
                            description="Top movers & market share"
                            onClick={navigateToDevView('leaderboard')}
                            isActive={isDevViewActive('leaderboard')}
                          />
                        )}
                      </Box>
                    </>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Coverage Accordion */}
              <Accordion
                elevation={0}
                sx={accordionSx(theme)}
                expanded={accordionOpen.benchmarks}
                onChange={toggleAccordion('benchmarks')}
              >
                <AccordionSummary
                  sx={accordionSummarySx(theme)}
                  expandIcon={<ArrowDropDownIcon />}
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', fontSize: '16px' }}
                  >
                    Coverage
                  </Box>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    color: theme.palette.text.secondary,
                    backgroundColor: 'transparent',
                    pt: 0,
                  }}
                >
                  <Box sx={menuSectionTitleSx(theme)}>Coverage</Box>

                  {/* Coverage Universe Hero Item */}
                  <Box
                    onClick={navigateToMarketInsightsCoverageView('market')}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      p: 1.25,
                      mt: 0.25,
                      mb: 0.25,
                      borderRadius: 1.25,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: theme.palette.mode === 'light'
                        ? 'rgba(63, 100, 168, 0.08)'
                        : 'rgba(145, 166, 205, 0.12)',
                      border: `1px solid ${theme.palette.mode === 'light'
                        ? 'rgba(63, 100, 168, 0.15)'
                        : 'rgba(145, 166, 205, 0.2)'}`,
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'light'
                          ? 'rgba(63, 100, 168, 0.15)'
                          : 'rgba(145, 166, 205, 0.2)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        backgroundColor: theme.palette.primary.main,
                        color: '#fff',
                      }}
                    >
                      <PublicIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Box sx={{ fontWeight: 600, fontSize: '14px', color: theme.palette.text.primary }}>
                        Coverage Universe
                      </Box>
                      <Box sx={{ fontSize: '11px', color: theme.palette.text.secondary }}>
                        Explore tracked companies
                      </Box>
                    </Box>
                  </Box>

                  {/* Coverage Sub-items */}
                  <Box sx={{ pl: 2, mt: 1 }}>
                    <MenuLink
                      to="/marketinsights/coverage"
                      icon={<PublicIcon />}
                      label="Coverage Table"
                      description="All tracked companies"
                      onClick={navigateToMarketInsightsCoverageView('market')}
                      isActive={isMarketInsightsCoverageViewActive('market')}
                    />
                  </Box>

                  {/* Divider */}
                  <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, mt: 2, mb: 1.5 }} />

                  {/* Acquisitions Hero Item */}
                  <Box
                    onClick={navigateToMarketInsightsAcquisitions}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      p: 1.25,
                      mt: 0.25,
                      mb: 0.25,
                      borderRadius: 1.25,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: theme.palette.mode === 'light'
                        ? 'rgba(63, 100, 168, 0.08)'
                        : 'rgba(145, 166, 205, 0.12)',
                      border: `1px solid ${theme.palette.mode === 'light'
                        ? 'rgba(63, 100, 168, 0.15)'
                        : 'rgba(145, 166, 205, 0.2)'}`,
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'light'
                          ? 'rgba(63, 100, 168, 0.15)'
                          : 'rgba(145, 166, 205, 0.2)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        backgroundColor: theme.palette.primary.main,
                        color: '#fff',
                      }}
                    >
                      <BusinessIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Box sx={{ fontWeight: 600, fontSize: '14px', color: theme.palette.text.primary }}>
                        Acquisitions
                      </Box>
                      <Box sx={{ fontSize: '11px', color: theme.palette.text.secondary }}>
                        Public software transactions
                      </Box>
                    </Box>
                  </Box>

                  {/* Venture Hero Item */}
                  <Box
                    onClick={navigateToVentureBacked}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      p: 1.25,
                      mt: 0.75,
                      mb: 0.25,
                      borderRadius: 1.25,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: theme.palette.mode === 'light'
                        ? 'rgba(63, 100, 168, 0.08)'
                        : 'rgba(145, 166, 205, 0.12)',
                      border: `1px solid ${theme.palette.mode === 'light'
                        ? 'rgba(63, 100, 168, 0.15)'
                        : 'rgba(145, 166, 205, 0.2)'}`,
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'light'
                          ? 'rgba(63, 100, 168, 0.15)'
                          : 'rgba(145, 166, 205, 0.2)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        backgroundColor: theme.palette.primary.main,
                        color: '#fff',
                      }}
                    >
                      <RocketLaunchIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Box sx={{ fontWeight: 600, fontSize: '14px', color: theme.palette.text.primary }}>
                        Venture
                      </Box>
                      <Box sx={{ fontSize: '11px', color: theme.palette.text.secondary }}>
                        Funding comps
                      </Box>
                    </Box>
                  </Box>

                  {SHOW_SI_HIRING && <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, mt: 2, mb: 1.5 }} />}

                  {SHOW_SI_HIRING && <Box sx={menuSectionTitleSx(theme)}>Partner Data</Box>}

                  {/* SI Hiring Hero Item */}
                  {SHOW_SI_HIRING && <Box
                    onClick={navigateToSIHiringView('vendor')}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      p: 1.25,
                      mt: 0.25,
                      mb: 0.25,
                      borderRadius: 1.25,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: theme.palette.mode === 'light'
                        ? 'rgba(63, 100, 168, 0.08)'
                        : 'rgba(145, 166, 205, 0.12)',
                      border: `1px solid ${theme.palette.mode === 'light'
                        ? 'rgba(63, 100, 168, 0.15)'
                        : 'rgba(145, 166, 205, 0.2)'}`,
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'light'
                          ? 'rgba(63, 100, 168, 0.15)'
                          : 'rgba(145, 166, 205, 0.2)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        backgroundColor: theme.palette.primary.main,
                        color: '#fff',
                      }}
                    >
                      <WorkIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Box sx={{ fontWeight: 600, fontSize: '14px', color: theme.palette.text.primary, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        SI Hiring
                        <Chip
                          label="Preview"
                          size="small"
                          sx={{
                            height: 14,
                            fontSize: '8px',
                            fontWeight: 600,
                            backgroundColor: theme.palette.warning.main,
                            color: theme.palette.warning.contrastText,
                            '& .MuiChip-label': { px: 0.5 },
                          }}
                        />
                      </Box>
                      <Box sx={{ fontSize: '11px', color: theme.palette.text.secondary }}>
                        Partner hiring trends
                      </Box>
                    </Box>
                  </Box>}

                  {/* SI Hiring Sub-items */}
                  {SHOW_SI_HIRING && <Box sx={{ pl: 2, mt: 1 }}>
                    <MenuLink
                      to={getSIHiringNavPath(SI_HIRING_SECTIONS.COMPANY, SI_HIRING_VIEWS.DASHBOARD)}
                      icon={<BusinessIcon />}
                      label="Detail"
                      description="Single-vendor partner hiring"
                      onClick={navigateToSIHiringView('detail')}
                      isActive={isSIHiringViewActive('detail')}
                    />
                    <MenuLink
                      to={getSIHiringNavPath(SI_HIRING_SECTIONS.VENDOR, SI_HIRING_VIEWS.TRENDS)}
                      icon={<InsightsIcon />}
                      label="Vendor"
                      description="Vendor hiring dashboard"
                      onClick={navigateToSIHiringView('vendor')}
                      isActive={isSIHiringViewActive('vendor')}
                    />
                    <MenuLink
                      to={getSIHiringNavPath(SI_HIRING_SECTIONS.CATEGORY, SI_HIRING_VIEWS.TRENDS)}
                      icon={<PublicIcon />}
                      label="Market"
                      description="Segmented and total partner demand"
                      onClick={navigateToSIHiringView('category')}
                      isActive={isSIHiringViewActive('category')}
                    />
                  </Box>}

                  {/* Divider */}
                  {SHOW_EXCHANGE_VOLUME && SHOW_SI_HIRING && <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, mt: 2, mb: 1.5 }} />}

                  {/* Exchange Volume Hero Item */}
                  {SHOW_EXCHANGE_VOLUME && <Box
                    onClick={navigateToExchangeVolumeView('trends')}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      p: 1.25,
                      mt: 0.25,
                      mb: 0.25,
                      borderRadius: 1.25,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: theme.palette.mode === 'light'
                        ? 'rgba(63, 100, 168, 0.08)'
                        : 'rgba(145, 166, 205, 0.12)',
                      border: `1px solid ${theme.palette.mode === 'light'
                        ? 'rgba(63, 100, 168, 0.15)'
                        : 'rgba(145, 166, 205, 0.2)'}`,
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'light'
                          ? 'rgba(63, 100, 168, 0.15)'
                          : 'rgba(145, 166, 205, 0.2)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        backgroundColor: theme.palette.primary.main,
                        color: '#fff',
                      }}
                    >
                      <CurrencyExchangeIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Box sx={{ fontWeight: 600, fontSize: '14px', color: theme.palette.text.primary, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        Exchange Volume
                        <Chip
                          label="Preview"
                          size="small"
                          sx={{
                            height: 14,
                            fontSize: '8px',
                            fontWeight: 600,
                            backgroundColor: theme.palette.warning.main,
                            color: theme.palette.warning.contrastText,
                            '& .MuiChip-label': { px: 0.5 },
                          }}
                        />
                      </Box>
                      <Box sx={{ fontSize: '11px', color: theme.palette.text.secondary }}>
                        Options & futures volume data
                      </Box>
                    </Box>
                  </Box>}

                  {/* Exchange Volume Sub-items */}
                  {SHOW_EXCHANGE_VOLUME && <Box sx={{ pl: 2, mt: 1 }}>
                    <MenuLink
                      to="/marketinsights/exchangevolume"
                      icon={<TimelineIcon />}
                      label="Volume Dashboard"
                      description="Track volume over time"
                      onClick={navigateToExchangeVolumeView('trends')}
                      isActive={isExchangeVolumeViewActive('trends')}
                    />
                    <MenuLink
                      to="/marketinsights/exchangevolume"
                      icon={<GridOnIcon />}
                      label="Data Table"
                      description="Detailed volume data"
                      onClick={navigateToExchangeVolumeView('table')}
                      isActive={isExchangeVolumeViewActive('table')}
                    />
                    <MenuLink
                      to="/marketinsights/exchangevolume"
                      icon={<DonutSmallIcon />}
                      label="Market Share"
                      description="Exchange breakdown"
                      onClick={navigateToExchangeVolumeView('marketShare')}
                      isActive={isExchangeVolumeViewActive('marketShare')}
                    />
                  </Box>}
                </AccordionDetails>
              </Accordion>
                </>
              )}

            </Drawer>
          </>
        )}
      </Box>
      </AppBar>
    </ThemeProvider>
  );
}

export default Header;
